/*
 * abi-bofa-filter-finder.js: behaviour for the BOFA filter finder V2 (sections/abi-bofa-filter-finder.liquid, context
 * "finder") and the E1 module on BOFA unit product pages (snippets/abi-bofa-extractor-filters.liquid, context "e1").
 * Theme asset, loaded with `defer`. Vanilla JS, no dependencies, no build step, ES2019.
 *
 * Spec:  Design Handoffs/BOFA Filter Finder/design_handoff_bofa_filter_finder_v2/ (README §4–§7, reference/interaction-notes.md,
 *        reference/copy-deck.md)
 * Hooks: review/bofa-filter-collection-build-2026-09-28/DOM-CONTRACT.md (+ J/CONTRACT-ADDITIONS.md)
 *
 * Structure
 *   1. core: pure logic (model resolution, part numbers, version state, persistence, money, counts, contact links, routes,
 *      Browse all). Exported for node tests: require('./abi-bofa-filter-finder.js'). Never touches window/document at
 *      require time.
 *   2. DOM: one controller per [data-abff] root. Liquid renders the shell, every state container and every <template>;
 *      this file parses the islands, reads the URL, clones templates, fills [data-slot]s (textContent only), toggles
 *      hidden / aria-* / data-state and sets hrefs.
 *
 * Every customer string goes through str(key, vars): the root's data-abff-config.strings first, then DEFAULT_STRINGS
 * (copy deck English).
 */
(function () {
  'use strict';

  /* ======================================================================================
   * 1. CORE (pure)
   * ==================================================================================== */
  var core = (function () {
    var DEFAULT_STRINGS = {
      /* page */
      h1_default: 'BOFA replacement filters',
      h1_model: 'Filters for {model}',
      h1_pn: 'Find by filter part number',
      h1_all: 'All BOFA filters',
      crumb_pn: 'Part number search',
      crumb_all: 'All BOFA filters',
      /* summary */
      eyebrow: 'Donaldson BOFA · {app}',
      count_line: '{n} filters · Order only the ones you’re replacing.',
      count_line_one: '1 filter · Order only the ones you’re replacing.',
      count_line_phone: '{n} filters · {app}',
      count_line_phone_one: '1 filter · {app}',
      unconfirmed_line: 'We haven’t confirmed which filters fit this model yet.',
      lifecycle_phased: 'No longer made by Donaldson · filters available',
      lifecycle_semi: 'Being phased out by Donaldson · filters available',
      change_model_aria: 'Change model, currently {model}',
      unconfirmed_h2: 'We’ll confirm the filters for your {model}',
      loading: 'Loading filters for {model}…',
      load_error: 'Couldn’t load the filters for this model. Try again, or send us a message and we’ll help.',
      /* rows */
      stage: 'Stage {i} of {n} · {role}',
      role_pre: 'Pre-filter',
      role_bag: 'Bag filter',
      role_combined: 'Combined filter',
      role_hepa: 'HEPA filter',
      role_gas: 'Gas filter',
      pn: 'Part no. {pn}',
      also: 'Also fits {list}',
      fits: 'Fits {list}',
      and_more: 'and {n} more',
      list_sep: ' · ',
      list_ref: 'Donaldson list {price}',
      per_filter: 'per filter',
      per_pack: '{pack}',
      available: 'Available to order',
      add: 'Add to cart',
      adding: 'Adding…',
      try_again: 'Try again',
      choose_first: 'Choose a version first',
      qty_aria: 'Quantity, {name}',
      nversions: '{n} versions · choose one',
      price_pending: 'Price shows once you choose a version.',
      legend: 'Choose filter version',
      hint: 'Match the part number on the label of the {role} you’re replacing.',
      unsure: 'I’m not sure',
      unsure_sub: 'Show me how to check',
      howto_title: 'How to check',
      howto_body: 'Look at the label on the {role} in your unit and match the part number: {list}.',
      howto_item: '{pn} ({short})',
      chosen: 'Version: {label}',
      chosen_short: 'Version: {short}',
      change_version: 'Change version',
      not_sold: 'Not sold online',
      check_supply: 'We’ll check if we can supply it.',
      stage_unconfirmed: 'Not confirmed yet',
      /* multi-select */
      include: 'Include in order',
      include_disabled: 'Include in order · choose a version first',
      include_aria: 'Include {name}, part no. {pn}, in the order',
      include_aria_choose: 'Include {name} in the order, choose a version first',
      mode_on: 'Selecting filters. Tick the filters to add together, then add them in one step.',
      mode_off: 'Buying filters one at a time.',
      count: '{p} {products} · {u} {units}',
      product: 'product',
      products: 'products',
      unit: 'unit',
      units: 'units',
      subtotal: 'Subtotal {amount} · before tax and shipping',
      subtotal_phone: 'Subtotal {amount}',
      empty: 'No filters selected yet · Tick at least one filter to add it.',
      bulk_add: 'Add selected to cart',
      bulk_error_net: 'Nothing was added. We couldn’t reach the cart. Your selection is kept, so you can try again.',
      bulk_error_422: 'Nothing was added. {message}',
      /* single add */
      row_error_net: 'Couldn’t add this filter. Nothing was added to your cart. Check your connection and try again.',
      row_error_422: 'Couldn’t add this filter: {message}. Nothing was added to your cart.',
      added_live: 'Added to cart',
      /* part number */
      pn_exact: 'Exact match for {pn}',
      pn_eyebrow: 'Exact part number match · {role}',
      pn_eyebrow_plain: 'Exact part number match',
      pn_oldlabel: 'Older BOFA labels show the same part number without the 1U, as {old}.',
      pn_nomatch: 'No filter matches “{typed}”',
      pn_dym: 'Did you mean {pn}?',
      dym_view: 'View {pn}',
      pn_fit_unconfirmed: 'Fit not confirmed yet',
      /* models */
      model_nomatch: 'No BOFA model matches “{typed}”',
      combobox_count: '{n} models found',
      combobox_count_one: '1 model found',
      combobox_none: 'No BOFA model matches “{typed}”',
      group_current: 'Current models',
      group_older: 'Older models · filters still sold',
      chip_unconfirmed: 'Not confirmed yet',
      sheet_count: '{n} models',
      sheet_count_one: '1 model',
      /* browse all */
      fit_unconfirmed: 'Fit not confirmed yet',
      all_status: 'Showing {x} of {y} {type} filters',
      all_status_all: 'Showing {x} of {y} filters',
      all_status_pre: 'Showing {x} of {y} pre-filters',
      type_combined: 'combined',
      type_hepa: 'HEPA',
      type_gas: 'gas and chemical',
      type_bag: 'bag',
      more: 'Show {n} more'
    };

    var TYPES = ['all', 'pre', 'combined', 'hepa', 'gas', 'bag'];
    var STATE_KEYS = ['model', 'pn', 'mode', 'type'];
    var PAGE_SIZE = 10;
    var ALSO_MAX = 4;

    function text(v) { return v == null ? '' : String(v); }
    function trim(v) { return text(v).replace(/^\s+|\s+$/g, ''); }
    function isObj(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
    function arr(v) { return Array.isArray(v) ? v : []; }
    function norm(t) { return text(t).toLowerCase().replace(/[^a-z0-9]/g, ''); }
    function find(list, fn) { for (var i = 0; i < list.length; i++) if (fn(list[i], i)) return list[i]; return null; }

    function format(tpl, vars) {
      return text(tpl).replace(/\{([a-z_]+)\}/gi, function (m, k) {
        return vars && vars[k] != null ? String(vars[k]) : '';
      });
    }
    /* island key names that mean the same string (L's config uses these) */
    var STRING_ALIASES = { pn: ['part_no'], dym_view: ['pn_dym_view'] };
    /* str(key, vars): island string when present, else the English default; unknown keys give ''. */
    function makeStr(strings) {
      var s = isObj(strings) ? strings : {};
      function raw(key) {
        if (typeof s[key] === 'string' && s[key] !== '') return s[key];
        var alt = STRING_ALIASES[key] || [];
        for (var i = 0; i < alt.length; i++) if (typeof s[alt[i]] === 'string' && s[alt[i]] !== '') return s[alt[i]];
        return DEFAULT_STRINGS[key] != null ? DEFAULT_STRINGS[key] : '';
      }
      function str(key, vars) { return format(raw(key), vars); }
      str.raw = raw;
      return str;
    }
    /* "Donaldson BOFA · " with no application → "Donaldson BOFA" */
    function dropEmptyTail(s) { return text(s).replace(/\s*·\s*$/, ''); }

    /* ---------------- the finder map ---------------- */
    function cleanVersion(v) {
      return {
        sku: trim(v.sku).toUpperCase(),
        label: text(v.label),
        condition: v.condition == null ? '' : text(v.condition),
        standard: !!v.standard,
        handle: v.handle ? String(v.handle) : null
      };
    }
    function cleanStage(s) {
      var versions = arr(s.versions).filter(function (v) { return isObj(v) && trim(v.sku); }).map(cleanVersion);
      return {
        key: trim(s.key),
        name: text(s.name),
        family: text(s.family),
        /* only 'confirmed' counts; missing, 'unconfirmed', 'not-confirmed' or anything else = not confirmed (help route) */
        status: s.status === 'confirmed' ? 'confirmed' : 'unconfirmed',
        component: s.component ? String(s.component) : null,
        versions: versions
      };
    }
    function cleanModel(m, i) {
      var aliases = arr(m.aliases).map(norm).filter(Boolean);
      return {
        slug: trim(m.slug),
        name: trim(m.name),
        group: text(m.group),
        chip: m.chip ? String(m.chip) : null,
        status: m.status === 'confirmed' ? 'confirmed' : 'unconfirmed',
        popular: m.popular || null,
        components_line: m.components_line ? String(m.components_line) : null,
        unit_handle: m.unit_handle ? String(m.unit_handle) : null,
        application: m.app ? String(m.app) : (m.application ? String(m.application) : null),
        aliases: aliases,
        order: i,
        stages: arr(m.stages).filter(function (s) { return isObj(s) && trim(s.key); }).map(cleanStage)
          .filter(function (s) { return s.versions.length > 0; })
      };
    }
    function pnKey(s) { return text(s).toUpperCase().replace(/[\s\-./]/g, ''); }

    /* Index the finder JSON (DOM-CONTRACT §1). Returns null when there are no usable models. */
    function indexMap(json) {
      var src = isObj(json) ? json : {};
      var models = arr(src.models).filter(function (m) { return isObj(m) && trim(m.slug) && trim(m.name); }).map(cleanModel);
      if (!models.length) return null;
      var bySlug = {};
      var keys = {};
      models.forEach(function (m) { if (!bySlug[m.slug]) bySlug[m.slug] = m; });
      /* B16: the map's names + aliases are authoritative (the generator moved "AD 1000", "BOFA 1000"… to the iQ models);
         a normalised slug ("ad1000" from ad-1000) is only a last-resort key, never ahead of an alias */
      var slugKeys = {};
      function addKey(k, m) { if (k && !keys[k]) keys[k] = m.slug; }
      models.forEach(function (m) { var sk = norm(m.slug); if (sk && !slugKeys[sk]) slugKeys[sk] = m.slug; });
      models.forEach(function (m) { addKey(norm(m.name), m); });
      models.forEach(function (m) { m.aliases.forEach(function (a) { addKey(a, m); }); });
      var filters = {};
      var byPn = {};
      var fsrc = isObj(src.filters) ? src.filters : {};
      Object.keys(fsrc).forEach(function (k) {
        var f = fsrc[k];
        if (!isObj(f)) return;
        var sku = trim(f.sku || k).toUpperCase();
        var entry = {
          sku: sku,
          role: f.role ? String(f.role) : null,
          chip: f.chip ? String(f.chip) : null,
          title: f.title ? String(f.title) : '',
          handle: f.handle ? String(f.handle) : null,
          fits: arr(f.fits).filter(function (x) { return isObj(x) && trim(x.model); }).map(function (x) {
            return { model: trim(x.model), name: text(x.name), conditional: !!x.conditional, condition: x.condition == null ? '' : text(x.condition) };
          })
        };
        filters[sku] = entry;
        var pk = pnKey(sku);
        if (pk && !byPn[pk]) byPn[pk] = entry;
      });
      var groups = arr(src.groups).filter(function (g) { return isObj(g) && g.key; }).map(function (g) { return { key: String(g.key), label: text(g.label) }; });
      var groupLabel = {};
      groups.forEach(function (g) { groupLabel[g.key] = g.label; });
      return { models: models, bySlug: bySlug, keys: keys, slugKeys: slugKeys, filters: filters, byPn: byPn, groups: groups, groupLabel: groupLabel, count: models.length };
    }

    /* "Advantage (AD) 350" / "BOFA AD 350" → "ad350" */
    function modelQueryNorms(value) {
      var k = norm(value);
      var out = [k];
      var noBrand = k.replace(/^bofa/, '');
      var adv = function (s) { return s.replace(/^advantage(ad)?/, 'ad'); };
      [noBrand, adv(k), adv(noBrand)].forEach(function (x) { if (x && out.indexOf(x) === -1) out.push(x); });
      return out.filter(Boolean);
    }
    /* ?model= value → model, or null. Order (B16): 1. the exact slug string (?model=ad-1000 opens the original);
       2. display names + the map's aliases, normalised; 3. a normalised slug, only when nothing else matched. */
    function resolveModel(index, value) {
      if (!index) return null;
      var raw = trim(value);
      if (!raw) return null;
      if (index.bySlug[raw.toLowerCase()]) return index.bySlug[raw.toLowerCase()];
      var tries = modelQueryNorms(raw);
      var i;
      for (i = 0; i < tries.length; i++) {
        if (index.keys[tries[i]]) return index.bySlug[index.keys[tries[i]]];
      }
      var sk = index.slugKeys || {};
      for (i = 0; i < tries.length; i++) {
        if (sk[tries[i]]) return index.bySlug[sk[tries[i]]];
      }
      return null;
    }
    function modelKeys(m) {
      var ks = [norm(m.name), norm(m.slug)];
      m.aliases.forEach(function (a) { if (ks.indexOf(a) === -1) ks.push(a); });
      return ks.filter(Boolean);
    }
    function groupOf(m) { return m.group === 'older' ? 'older' : 'current'; }
    var collator = null;
    function byName(a, b) {
      try {
        if (!collator && typeof Intl !== 'undefined' && Intl.Collator) collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
      } catch (e) { collator = null; }
      if (collator) return collator.compare(a.name, b.name);
      return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0);
    }
    /* Combobox options: grouped Current then Older; inside a group exact → starts-with → contains, then by name.
       An empty query lists every model (the full list). Never picks one. */
    function searchModels(index, query) {
      if (!index) return [];
      var qs = modelQueryNorms(query);
      var q = qs[0] || '';
      var out = [];
      index.models.forEach(function (m) {
        var score = 99;
        if (!q) score = 0;
        else {
          modelKeys(m).forEach(function (k) {
            qs.forEach(function (qq) {
              if (k === qq) score = Math.min(score, 0);
              else if (k.indexOf(qq) === 0) score = Math.min(score, 1);
              else if (k.indexOf(qq) > 0) score = Math.min(score, 2);
            });
          });
        }
        if (score < 99) out.push({ model: m, group: groupOf(m), score: score });
      });
      out.sort(function (a, b) {
        if (a.group !== b.group) return a.group === 'current' ? -1 : 1;
        return (a.score - b.score) || (a.model.order - b.model.order);
      });
      return out;
    }

    /* Optimal-string-alignment Damerau-Levenshtein (adjacent swap = 1). Same as full DL at distance ≤ 1. */
    function dlDistance(a, b) {
      a = text(a);
      b = text(b);
      var n = a.length;
      var m = b.length;
      if (!n) return m;
      if (!m) return n;
      var d = [];
      var i;
      var j;
      for (i = 0; i <= n; i++) { d[i] = [i]; }
      for (j = 0; j <= m; j++) d[0][j] = j;
      for (i = 1; i <= n; i++) {
        for (j = 1; j <= m; j++) {
          var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
          var v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
          if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) v = Math.min(v, d[i - 2][j - 2] + 1);
          d[i][j] = v;
        }
      }
      return d[n][m];
    }
    function commonPrefix(a, b) {
      var n = 0;
      while (n < a.length && n < b.length && a.charAt(n) === b.charAt(n)) n++;
      return n;
    }
    /* ≤ `limit` (3) suggestions for an unknown model (never auto-navigates). Per key, best of: exact; the query is the
       start of the key ("AD 100" → AD 1000 …) or the key the start of the query ("AD 3500" → AD 350); contained; whole-key
       edit distance; and one edit (incl. an adjacent swap) against any same-length stretch of the key, so "Oracel" finds
       the Oracle models. Ties: longer shared start, then map order. */
    function suggestModels(index, typed, limit) {
      if (!index) return [];
      var qs = modelQueryNorms(typed);
      if (!qs.length) return [];
      var scored = [];
      index.models.forEach(function (m) {
        var best = 99;
        modelKeys(m).forEach(function (k) {
          qs.forEach(function (q) {
            if (k.length < 2 || !q) return;
            var lcp = commonPrefix(q, k) / 100;
            var s = 99;
            if (k === q) s = 0;
            else if (k.indexOf(q) === 0) s = 0.1 + (k.length - q.length) / 1000;
            else if (q.indexOf(k) === 0 && k.length >= 3) s = 0.2 + (q.length - k.length) / 1000;
            else if (q.length >= 3 && k.indexOf(q) > 0) s = 0.3;
            else {
              var d = dlDistance(q, k);
              var allowed = Math.max(1, Math.floor(Math.min(q.length, k.length) / 3));
              if (d <= allowed) s = 1 + d - lcp;
              if (q.length >= 4) {
                for (var i = 0; i + q.length - 1 <= k.length; i++) {
                  for (var L = q.length - 1; L <= q.length + 1; L++) {
                    if (i + L > k.length) continue;
                    var d2 = dlDistance(q, k.substr(i, L));
                    if (d2 <= 1) s = Math.min(s, 1.5 + d2 + (i > 0 ? 0.1 : 0) - lcp);
                  }
                }
              }
            }
            best = Math.min(best, s);
          });
        });
        if (best < 99) scored.push({ model: m, score: best });
      });
      scored.sort(function (a, b) { return (a.score - b.score) || (a.model.order - b.model.order); });
      return scored.slice(0, limit || 3).map(function (s) { return s.model; });
    }

    /* ---------------- part numbers (README §5.3) ---------------- */
    function normPn(input) {
      var typed = trim(input);
      var t = pnKey(typed);
      if (!t) return { pn: '', typed: typed, had1U: false };
      var had = t.indexOf('1U') === 0;
      return { pn: had ? t : '1U' + t, typed: typed, had1U: had };
    }
    function oldLabel(pn) { return text(pn).replace(/^1U/, ''); }
    /* Exact match in the map's filter index, else a live product with that SKU (not in the map). */
    function findPn(index, products, pn) {
      var k = pnKey(pn);
      if (!k) return null;
      if (index && index.byPn[k]) return index.byPn[k];
      var p = products && products.bySku[k];
      if (p) return { sku: p.sku, role: null, chip: null, title: p.title, handle: p.handle, fits: [], unmapped: true };
      return null;
    }
    /* "Did you mean": exactly one known part number at edit distance 1, else null. */
    function didYouMean(index, products, pn) {
      var k = pnKey(pn);
      if (!k || findPn(index, products, k)) return null;
      var seen = {};
      var hits = [];
      function consider(key, entry) {
        if (seen[key] || key.indexOf('1U') !== 0) return;
        seen[key] = true;
        if (Math.abs(key.length - k.length) > 1) return;
        if (dlDistance(k, key) === 1) hits.push(entry);
      }
      if (index) Object.keys(index.byPn).forEach(function (key) { consider(key, index.byPn[key]); });
      if (products) Object.keys(products.bySku).forEach(function (key) {
        var p = products.bySku[key];
        consider(key, { sku: p.sku, role: null, chip: null, title: p.title, handle: p.handle, fits: [], unmapped: true });
      });
      return hits.length === 1 ? hits[0] : null;
    }

    /* ---------------- labels ---------------- */
    var FAMILY_ROLE = { pleat: 'role_pre', bag: 'role_bag', combined: 'role_combined', hepa: 'role_hepa', gas: 'role_gas', chemical: 'role_gas' };
    var CHIP_ROLE = { pre: 'role_pre', bag: 'role_bag', combined: 'role_combined', hepa: 'role_hepa', gas: 'role_gas' };
    function roleOf(stage, str) {
      var k = stage && FAMILY_ROLE[stage.family];
      return k ? str(k) : text(stage && stage.name);
    }
    function chipRole(chip, str) { return CHIP_ROLE[chip] ? str(CHIP_ROLE[chip]) : ''; }
    /* "Pre-filter" → "pre-filter", "Combined filter" → "combined filter", "HEPA filter" stays */
    function lowerRole(role) {
      var r = text(role);
      return /^[A-Z][a-z]/.test(r) ? r.charAt(0).toLowerCase() + r.slice(1) : r;
    }
    function isGrouped(model) { return !!(model && model.stages.some(function (s) { return !!s.component; })); }
    function eyebrowFor(model, stage, i, n, str) {
      var role = roleOf(stage, str);
      if (isGrouped(model) || n <= 1) return role;
      return str('stage', { i: i + 1, n: n, role: role });
    }
    /* The version label without the stage's own words: "Standard combined filter" → "standard", "NOX combined filter" → "NOX" */
    function shortLabel(label, stageName) {
      var full = trim(label);
      var stop = {};
      text(stageName).toLowerCase().split(/\s+/).forEach(function (w) {
        var c = w.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
        if (!c) return;
        stop[c] = true;
        c.split('-').forEach(function (p) { if (p) stop[p] = true; });
      });
      var kept = full.split(/\s+/).filter(function (tok) {
        var c = tok.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
        return c && !stop[c];
      });
      var s = kept.join(' ').replace(/^[,;\s]+|[,;\s]+$/g, '');
      var opens = (s.match(/\(/g) || []).length;
      var closes = (s.match(/\)/g) || []).length;
      if (!s || opens !== closes || s === full) return full;
      /* it is shown inside parentheses: "lower-grade (F6)" → "lower-grade, F6" */
      s = s.replace(/\s*\(([^()]*)\)/g, ', $1').replace(/^,\s*/, '');
      /* a plain capitalised first word reads lower case inside the list ("Standard" → "standard"; NOX, F9, SafeChange stay) */
      if (/^[A-Z][a-z]+(-[a-z]+)*(?=[\s,]|$)/.test(s)) s = s.charAt(0).toLowerCase() + s.slice(1);
      return s;
    }
    /* "Pre-filter pad (pack of 5)" → "pack of 5"; "(twin pack)" / ", twin pack" → "twin pack"; "(3 pack)" → "3 pack" */
    function packOf() {
      for (var i = 0; i < arguments.length; i++) {
        var s = text(arguments[i]);
        var m = /\bpack of (\d+|five|two|three|four|ten)\b/i.exec(s);
        if (m) return 'pack of ' + m[1].toLowerCase();
        m = /\b(\d+) pack\b/i.exec(s);
        if (m) return m[1] + ' pack';
        if (/\btwin pack\b/i.test(s)) return 'twin pack';
      }
      return '';
    }
    function unitText(str) {
      var pack = packOf.apply(null, Array.prototype.slice.call(arguments, 1));
      return pack ? str('per_pack', { pack: pack }) : str('per_filter');
    }
    /* Model names from a filter's fits, unique by model, without `exclude` */
    function fitNames(filter, exclude) {
      var seen = {};
      var out = [];
      arr(filter && filter.fits).forEach(function (x) {
        if (!x.model || x.model === exclude || seen[x.model]) return;
        seen[x.model] = true;
        out.push(x.name || x.model);
      });
      return out;
    }
    /* "A · B · C · D and 3 more" (max 4) */
    function listText(names, max, str) {
      var lim = max || ALSO_MAX;
      var shown = names.slice(0, lim).join(str('list_sep'));
      var rest = names.length - lim;
      return rest > 0 ? shown + ' ' + str('and_more', { n: rest }) : shown;
    }
    function alsoFitsText(index, sku, currentSlug, str) {
      var f = index && index.filters[sku];
      var names = fitNames(f, currentSlug);
      return names.length ? str('also', { list: listText(names, ALSO_MAX, str) }) : '';
    }

    /* ---------------- money ---------------- */
    function decodeEntities(s) {
      return text(s).replace(/<[^>]*>/g, '')
        .replace(/&#(\d+);/g, function (m, n) { return String.fromCharCode(parseInt(n, 10)); })
        .replace(/&#x([0-9a-f]+);/gi, function (m, n) { return String.fromCharCode(parseInt(n, 16)); })
        .replace(/&quot;/g, '"').replace(/&#39;|&apos;/g, '\'').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&');
    }
    function delimit(cents, precision, thousands, decimal) {
      var n = Math.round(Number(cents) || 0);
      var neg = n < 0;
      var value = (Math.abs(n) / 100).toFixed(precision);
      var parts = value.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return (neg ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
    }
    /* Shopify money_format: {{amount}}, {{amount_no_decimals}} (+ the comma / space / apostrophe variants) */
    function formatMoney(cents, fmt) {
      var f = decodeEntities(typeof fmt === 'string' && fmt ? fmt : '${{amount}}');
      var re = /\{\{\s*(\w+)\s*\}\}/;
      var m = re.exec(f);
      if (!m) return f + delimit(cents, 2, ',', '.');
      var v;
      switch (m[1]) {
        case 'amount_no_decimals': v = delimit(cents, 0, ',', '.'); break;
        case 'amount_with_comma_separator': v = delimit(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': v = delimit(cents, 0, '.', ','); break;
        case 'amount_with_space_separator': v = delimit(cents, 2, ' ', ','); break;
        case 'amount_no_decimals_with_space_separator': v = delimit(cents, 0, ' ', ','); break;
        case 'amount_with_apostrophe_separator': v = delimit(cents, 2, '\'', '.'); break;
        default: v = delimit(cents, 2, ',', '.');
      }
      return f.replace(re, v);
    }
    function toCents(v) {
      if (typeof v === 'number') return isFinite(v) ? Math.round(v) : null;
      var s = trim(v);
      if (!s) return null;
      if (/^\d+$/.test(s)) return parseInt(s, 10);
      var f = parseFloat(s.replace(/[^0-9.\-]/g, ''));
      return isFinite(f) ? Math.round(f * 100) : null;
    }

    /* ---------------- products island ---------------- */
    function parseProducts(json) {
      var out = { bySku: {}, list: [], moneyFormat: '${{amount}}', units: {} };
      if (!isObj(json)) return out;
      if (typeof json._money_format === 'string' && json._money_format) out.moneyFormat = json._money_format;
      if (isObj(json._units)) out.units = json._units;
      Object.keys(json).forEach(function (k) {
        if (k.charAt(0) === '_') return;
        var p = json[k];
        if (!isObj(p)) return;
        var sku = pnKey(p.sku || k);
        var vid = p.variant_id != null ? Number(p.variant_id) : NaN;
        var price = toCents(p.price);
        if (!sku || !isFinite(vid) || !trim(p.url) || !trim(p.handle) || price == null || out.bySku[sku]) return;
        var cmp = toCents(p.compare_at);
        var entry = {
          sku: sku,
          variant_id: vid,
          product_id: p.product_id != null ? Number(p.product_id) : null,
          handle: String(p.handle),
          url: String(p.url),
          title: trim(p.title),
          price: price,
          compare_at: cmp != null && cmp > price ? cmp : null,
          available: p.available !== false,
          image: p.image ? String(p.image) : null,
          image_alt: p.image_alt ? String(p.image_alt) : ''
        };
        out.bySku[sku] = entry;
        out.list.push(entry);
      });
      return out;
    }
    /* A version is buyable only when its SKU is in the products island (DOM-CONTRACT §1) and, with
       `unavailableNotSold` (the default, config `unavailable_not_sold`), available: an Add that would 422 is never shown;
       the row reads "Not sold online · Send us a message". Browse all still lists the product (it only links). */
    function soldProduct(products, sku, unavailableNotSold) {
      var p = products && products.bySku[pnKey(sku)];
      if (!p) return null;
      if (unavailableNotSold && !p.available) return null;
      return p;
    }

    /* ---------------- rows and version choice ---------------- */
    function rowPlan(stage, choice, sold) {
      var vs = stage ? stage.versions : [];
      var multi = vs.length > 1;
      var unconfirmed = !stage || stage.status !== 'confirmed' || !vs.length;
      var version = null;
      if (!multi) version = vs[0] || null;
      else if (choice) version = find(vs, function (v) { return v.sku === choice; });
      var product = version && !unconfirmed ? sold(version.sku) : null;
      var state;
      if (unconfirmed) state = 'notsold';
      else if (multi && !version) state = 'choose';
      else if (product) state = multi ? 'chosen' : 'buy';
      else state = 'notsold';
      return {
        multi: multi,
        unconfirmed: unconfirmed,
        version: version,
        sku: version ? version.sku : '',
        product: product,
        state: state,
        buyable: state === 'buy' || state === 'chosen',
        anySold: !unconfirmed && vs.some(function (v) { return !!sold(v.sku); })
      };
    }
    function storageKey(prefix, slug) { return (typeof prefix === 'string' && prefix ? prefix : 'abi-bofa-versions:') + slug; }
    /* Keep only choices that still make sense: a multi-version confirmed stage, a part number the mapping lists for it,
       and not a version that had a product (handle) and lost it. Returns { choices, changed }. */
    function cleanChoices(raw, model, sold) {
      var out = {};
      var changed = false;
      var src = isObj(raw) ? raw : {};
      Object.keys(src).forEach(function (k) {
        var stage = model ? find(model.stages, function (s) { return s.key === k; }) : null;
        var sku = typeof src[k] === 'string' ? src[k].toUpperCase() : '';
        var v = stage && stage.status === 'confirmed' && stage.versions.length > 1 ? find(stage.versions, function (x) { return x.sku === sku; }) : null;
        if (!v || (v.handle && !sold(v.sku))) { changed = true; return; }
        out[k] = v.sku;
        if (src[k] !== v.sku) changed = true;
      });
      return { choices: out, changed: changed };
    }
    var memStore = {};
    function readChoices(storage, key) {
      var raw = null;
      try {
        raw = storage ? storage.getItem(key) : null;
      } catch (e) {
        raw = null;
      }
      if (raw == null && memStore[key]) return JSON.parse(JSON.stringify(memStore[key]));
      if (!raw) return {};
      try {
        var o = JSON.parse(raw);
        return isObj(o) ? o : {};
      } catch (e) {
        return {};
      }
    }
    function writeChoices(storage, key, choices) {
      var empty = !choices || !Object.keys(choices).length;
      memStore[key] = empty ? null : JSON.parse(JSON.stringify(choices));
      try {
        if (!storage) return false;
        if (empty) storage.removeItem(key);
        else storage.setItem(key, JSON.stringify(choices));
        return true;
      } catch (e) {
        return false;
      }
    }
    /* "1UA1030055 (standard), 1UA1030355 (NOX), 1UA1030425 (MEK)" */
    function howtoList(stage, str) {
      return stage.versions.map(function (v) { return str('howto_item', { pn: v.sku, short: shortLabel(v.label, stage.name) }); }).join(', ');
    }

    /* ---------------- multi-select ---------------- */
    function selection(rows) {
      var p = 0;
      var u = 0;
      var cents = 0;
      rows.forEach(function (r) {
        if (!r.included || !r.price && r.price !== 0) return;
        var q = clampQty(r.qty) || 1;
        p += 1;
        u += q;
        cents += r.price * q;
      });
      return { products: p, units: u, subtotal: cents };
    }
    function countText(p, u, str) {
      return str('count', { p: p, u: u, products: str(p === 1 ? 'product' : 'products'), units: str(u === 1 ? 'unit' : 'units') });
    }
    function clampQty(v) {
      var n = typeof v === 'number' ? v : parseInt(trim(v), 10);
      if (!isFinite(n) || isNaN(n)) return null;
      n = Math.floor(n);
      return Math.max(1, Math.min(99, n));
    }
    /* Shopify 422 names a line: the island title (without "(Part No…)") appears in the message */
    function rowForMessage(message, rows) {
      var msg = text(message).toLowerCase();
      if (!msg) return null;
      return find(rows, function (r) {
        var t = r.title ? String(r.title).toLowerCase() : '';
        return (t && msg.indexOf(t) !== -1) || (r.sku && msg.indexOf(String(r.sku).toLowerCase()) !== -1);
      });
    }
    function stripEndPeriod(s) { return trim(s).replace(/[.。]+$/, ''); }

    /* ---------------- contact links ---------------- */
    function contactHref(base, topic, params) {
      var b = trim(base) || '/pages/contact';
      var q = ['topic=' + encodeURIComponent(topic || 'filters')];
      ['model', 'pn', 'from'].forEach(function (k) {
        var v = params ? trim(params[k]) : '';
        if (v) q.push(k + '=' + encodeURIComponent(v));
      });
      return b + (b.indexOf('?') === -1 ? '?' : '&') + q.join('&');
    }

    /* ---------------- routes ---------------- */
    function decodePart(s) {
      try { return decodeURIComponent(text(s).replace(/\+/g, ' ')); } catch (e) { return text(s); }
    }
    /* ?page= other than 1 (Liquid paginate would page the products island to empty): the same search without any page
       pair, every other pair byte for byte; null when there is nothing to strip */
    function stripPageParam(search) {
      var pairs = text(search).replace(/^\?/, '').split('&').filter(Boolean);
      var isPage = function (pr) { var at = pr.indexOf('='); return decodePart(at === -1 ? pr : pr.slice(0, at)) === 'page'; };
      var pages = pairs.filter(isPage);
      if (!pages.length) return null;
      var harmful = pages.some(function (pr) { var at = pr.indexOf('='); return trim(at === -1 ? '' : decodePart(pr.slice(at + 1))) !== '1'; });
      if (!harmful) return null;
      var keep = pairs.filter(function (pr) { return !isPage(pr); });
      return keep.length ? '?' + keep.join('&') : '';
    }
    function parseRoute(search, hash) {
      var pairs = text(search).replace(/^\?/, '').split('&').filter(Boolean);
      var p = {};
      pairs.forEach(function (pr) {
        var at = pr.indexOf('=');
        var k = decodePart(at === -1 ? pr : pr.slice(0, at));
        var v = at === -1 ? '' : decodePart(pr.slice(at + 1));
        if (!(k in p)) p[k] = v;
      });
      var r = { kind: 'default', model: null, pn: null, type: 'all', where: /^#where-to-find/.test(text(hash)) };
      if (trim(p.model)) { r.kind = 'model'; r.model = trim(p.model); }
      else if ('pn' in p) { r.kind = 'pn'; r.pn = p.pn; }
      else if (p.mode === 'all') { r.kind = 'all'; r.type = TYPES.indexOf(p.type) !== -1 ? p.type : 'all'; }
      return r;
    }
    function stateQuery(route) {
      if (!route) return [];
      if (route.kind === 'model') return [['model', route.model]];
      if (route.kind === 'pn') return [['pn', route.pn == null ? '' : route.pn]];
      if (route.kind === 'all') return route.type && route.type !== 'all' ? [['mode', 'all'], ['type', route.type]] : [['mode', 'all']];
      return [];
    }
    /* Rewrites only model / pn / mode / type; every other pair (preview_theme_id, view, …) is kept byte for byte. */
    function routeSearch(search, route) {
      var keep = text(search).replace(/^\?/, '').split('&').filter(Boolean).filter(function (pr) {
        var at = pr.indexOf('=');
        return STATE_KEYS.indexOf(decodePart(at === -1 ? pr : pr.slice(0, at))) === -1;
      });
      stateQuery(route).forEach(function (kv) { keep.push(kv[0] + '=' + encodeURIComponent(kv[1])); });
      return keep.length ? '?' + keep.join('&') : '';
    }
    function routeHref(path, search, route, hash) {
      return text(path) + routeSearch(search, route) + (hash ? (hash.charAt(0) === '#' ? hash : '#' + hash) : '');
    }
    /* `from` for contact links: the finder path + the state only (never preview parameters) */
    function fromFor(path, route) {
      var q = stateQuery(route).map(function (kv) { return kv[0] + '=' + encodeURIComponent(kv[1]); }).join('&');
      return text(path) + (q ? '?' + q : '');
    }

    /* ---------------- Browse all ---------------- */
    function browseRows(index, products) {
      return (products ? products.list : []).map(function (p) {
        var f = index ? index.filters[p.sku] || index.byPn[pnKey(p.sku)] : null;
        var type = f && TYPES.indexOf(f.chip) > 0 ? f.chip : 'other';
        return { sku: p.sku, product: p, type: type, fits: fitNames(f, null), role: f && f.role ? f.role : '' };
      });
    }
    function typeCounts(rows) {
      var c = { all: rows.length, pre: 0, combined: 0, hepa: 0, gas: 0, bag: 0 };
      rows.forEach(function (r) { if (c[r.type] != null && r.type !== 'all') c[r.type] += 1; });
      return c;
    }
    function browseList(rows, type, sort) {
      var list = !type || type === 'all' ? rows.slice() : rows.filter(function (r) { return r.type === type; });
      list.sort(function (a, b) {
        if (sort === 'price') {
          var d = a.product.price - b.product.price;
          if (d) return d;
        }
        return a.sku < b.sku ? -1 : (a.sku > b.sku ? 1 : 0);
      });
      return list;
    }
    function browseStatus(x, y, type, str) {
      if (!type || type === 'all') return str('all_status_all', { x: x, y: y });
      if (str.raw('all_status_' + type)) return str('all_status_' + type, { x: x, y: y });
      return str('all_status', { x: x, y: y, type: str('type_' + type) || type });
    }

    return {
      DEFAULT_STRINGS: DEFAULT_STRINGS,
      TYPES: TYPES,
      PAGE_SIZE: PAGE_SIZE,
      norm: norm,
      format: format,
      makeStr: makeStr,
      dropEmptyTail: dropEmptyTail,
      indexMap: indexMap,
      resolveModel: resolveModel,
      searchModels: searchModels,
      suggestModels: suggestModels,
      groupOf: groupOf,
      dlDistance: dlDistance,
      pnKey: pnKey,
      normPn: normPn,
      oldLabel: oldLabel,
      findPn: findPn,
      didYouMean: didYouMean,
      roleOf: roleOf,
      chipRole: chipRole,
      lowerRole: lowerRole,
      isGrouped: isGrouped,
      eyebrowFor: eyebrowFor,
      shortLabel: shortLabel,
      packOf: packOf,
      unitText: unitText,
      fitNames: fitNames,
      listText: listText,
      alsoFitsText: alsoFitsText,
      formatMoney: formatMoney,
      toCents: toCents,
      parseProducts: parseProducts,
      soldProduct: soldProduct,
      rowPlan: rowPlan,
      storageKey: storageKey,
      cleanChoices: cleanChoices,
      readChoices: readChoices,
      writeChoices: writeChoices,
      howtoList: howtoList,
      selection: selection,
      countText: countText,
      clampQty: clampQty,
      rowForMessage: rowForMessage,
      stripEndPeriod: stripEndPeriod,
      contactHref: contactHref,
      parseRoute: parseRoute,
      stripPageParam: stripPageParam,
      stateQuery: stateQuery,
      routeSearch: routeSearch,
      routeHref: routeHref,
      fromFor: fromFor,
      browseRows: browseRows,
      typeCounts: typeCounts,
      browseList: browseList,
      browseStatus: browseStatus
    };
  })();

  if (typeof module === 'object' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* ======================================================================================
   * 2. DOM
   * ==================================================================================== */
  var CART_TIMEOUT = 15000;
  var PHONE_MQ = '(max-width: 767.98px)';
  var ID_REFS = ['for', 'aria-describedby', 'aria-labelledby', 'aria-controls', 'aria-owns', 'aria-activedescendant', 'aria-errormessage'];
  var cart = { busy: false, chain: null };
  var boxSeq = 0;

  function qs(el, sel) { return el ? el.querySelector(sel) : null; }
  function qsa(el, sel) { return el ? Array.prototype.slice.call(el.querySelectorAll(sel)) : []; }
  function setHidden(el, hide) {
    if (!el) return;
    if (hide) { if (!el.hasAttribute('hidden')) el.setAttribute('hidden', ''); }
    else if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
  }
  function hideAll(list, hide) { list.forEach(function (el) { setHidden(el, hide); }); }
  function setText(el, t) { if (el) el.textContent = t == null ? '' : String(t); }
  function fillSlots(scope, name, t) { qsa(scope, '[data-slot="' + name + '"]').forEach(function (el) { setText(el, t); }); }
  /* fill and hide-when-empty */
  function slotShow(scope, name, t) {
    qsa(scope, '[data-slot="' + name + '"]').forEach(function (el) { setText(el, t); setHidden(el, !t); });
  }
  function setAttr(el, name, value) {
    if (!el) return;
    if (value == null || value === false) el.removeAttribute(name);
    else el.setAttribute(name, value === true ? '' : String(value));
  }
  function readJson(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || 'null'); } catch (e) { return null; }
  }
  function mq(query) { try { return window.matchMedia ? window.matchMedia(query) : null; } catch (e) { return null; } }
  var phoneMq = mq(PHONE_MQ);
  var reduceMq = mq('(prefers-reduced-motion: reduce)');
  function isPhone() { return !!(phoneMq && phoneMq.matches); }
  function onMqChange(m, fn) {
    if (!m) return;
    if (m.addEventListener) m.addEventListener('change', fn);
    else if (m.addListener) m.addListener(fn);
  }
  function reducedMotion() { return !!(reduceMq && reduceMq.matches); }
  function raf(fn) {
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  }
  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) { /* not focusable */ } }
  }
  function focusable(el) {
    if (!el) return null;
    var tag = el.tagName;
    if (!/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(tag) && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    return el;
  }
  function inHidden(el) { return !el || !el.isConnected || !!(el.closest && el.closest('[hidden]')); }
  function sessionStore() { try { return window.sessionStorage; } catch (e) { return null; } }
  function templateNode(tpl) {
    if (!tpl) return null;
    var src = tpl.content ? tpl.content.firstElementChild : tpl.firstElementChild;
    return src ? src.cloneNode(true) : null;
  }
  /* Ids inside a template clone get a suffix; references inside the clone follow them. */
  function uniquifyIds(node, suffix) {
    if (!node) return;
    var map = {};
    var all = [node].concat(qsa(node, '[id]'));
    all.forEach(function (el) {
      if (!el.id) return;
      var next = el.id + suffix;
      map[el.id] = next;
      el.id = next;
    });
    if (!Object.keys(map).length) return;
    [node].concat(qsa(node, '*')).forEach(function (el) {
      ID_REFS.forEach(function (a) {
        var v = el.getAttribute(a);
        if (!v) return;
        var nv = v.split(/\s+/).map(function (t) { return map[t] || t; }).join(' ');
        if (nv !== v) el.setAttribute(a, nv);
      });
      var href = el.getAttribute('href');
      if (href && href.charAt(0) === '#' && map[href.slice(1)]) el.setAttribute('href', '#' + map[href.slice(1)]);
    });
  }
  /* elements under `el` matching `sel`, except those inside a version-card set, a card or the how-to panel */
  var NESTED = '[data-abff-versions], [data-abff-versions-set], [data-abff-vcard], [data-abff-howto]';
  function ownAll(el, sel) {
    return qsa(el, sel).filter(function (x) {
      var up = x.parentNode && x.parentNode.closest ? x.parentNode.closest(NESTED) : null;
      return !up || !el.contains(up);
    });
  }
  function slotShowIn(el, name, t) {
    ownAll(el, '[data-slot="' + name + '"]').forEach(function (e) { setText(e, t); setHidden(e, !t); });
  }
  function ensureId(el, id) { if (el && !el.id) el.id = id; return el ? el.id : ''; }
  /* A slot inside a sentence L wrote (No filter matches “<span data-slot="typed">”) takes the value only; a slot on
     its own takes the whole sentence. */
  function embedded(el) {
    var p = el && el.parentNode;
    if (!p || /^(P|DIV|LI|H[1-6]|SECTION|ARTICLE)$/.test(el.tagName)) return false;
    for (var n = p.firstChild; n; n = n.nextSibling) {
      if (n !== el && String(n.textContent || '').replace(/\s+/g, '') !== '') return true;
    }
    return false;
  }
  function fillSentence(scope, slot, sentence, value) {
    var first = null;
    qsa(scope, '[data-slot="' + slot + '"]').forEach(function (el) {
      var inside = embedded(el);
      setText(el, inside ? value : sentence);
      if (!first) first = inside ? el.parentNode : el;
    });
    return first;
  }
  function scrollY() { return window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0; }

  /* ---------- Webflow's anchor scroll ----------
     The theme loads the Webflow runtime (assets/h1-custom.js). Its jQuery handler on document ("click.wf-scroll") takes
     every same-page #id link, ignores preventDefault and scrolls with no header offset. Remove that one handler: now,
     when Webflow is ready, on DOMContentLoaded and on window load. (Port of uls-part-pdp.js.) */
  function disableWebflowAnchorScroll() {
    var off = function () {
      try { if (window.jQuery) window.jQuery(document).off('click.wf-scroll'); } catch (e) { /* no jQuery */ }
    };
    off();
    try { if (window.Webflow && typeof window.Webflow.push === 'function') window.Webflow.push(off); } catch (e) { /* no Webflow */ }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(off, 0); });
    window.addEventListener('load', off);
  }

  /* ------------------------------------------------------------------------------------ */
  function initRoot(root) {
    if (!root || root.__abff) return;
    var config = readJson(qs(root, 'script[data-abff-config]'));
    if (!config || typeof config !== 'object') config = {};
    var context = root.getAttribute('data-abff-context') === 'e1' ? 'e1' : 'finder';
    var ctx = {
      root: root,
      context: context,
      config: config,
      str: core.makeStr(config.strings),
      sid: String(root.id || 'abff').replace(/^abff-/, '') || String(++boxSeq),
      products: core.parseProducts(readJson(qs(root, 'script[data-abff-products]'))),
      index: null,
      model: null,
      route: null,
      view: null,
      rows: [],
      pnRow: null,
      choices: {},
      mode: 'single',
      busy: false,
      rowSeq: 0,
      headerH: 0,
      browse: { type: 'all', sort: 'pn', shown: core.PAGE_SIZE, rows: null },
      bulkError: null,
      sheet: null,
      boxes: []
    };
    root.__abff = ctx;
    root.setAttribute('data-abff-js', 'on');
    ctx.money = function (cents) { return core.formatMoney(cents, ctx.products.moneyFormat); };
    ctx.sold = function (sku) { return core.soldProduct(ctx.products, sku, config.unavailable_not_sold !== false); };
    ctx.announce = function (msg) {
      var region = qs(root, '[data-abff-live]');
      if (!region || !msg) return;
      region.textContent = '';
      setTimeout(function () { region.textContent = msg; }, 40);
    };
    if (context === 'e1') {
      var island = readJson(qs(root, 'script[data-abff-model]'));
      var modelJson = island && island.model && typeof island.model === 'object' ? island.model : island;
      var filtersJson = (island && island.filters) || readJson(qs(root, 'script[data-abff-filters]')) || {};
      ctx.index = core.indexMap({ models: modelJson ? [modelJson] : [], filters: filtersJson, groups: [] });
    } else {
      ctx.index = core.indexMap(readJson(qs(root, 'script[data-abff-map]')));
    }
    initMeasure(ctx);
    initPlaceholders(ctx);
    initRowEvents(ctx);
    if (context === 'e1') initE1(ctx);
    else initFinder(ctx);
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted || !ctx.model || !root.isConnected) return;
      /* back/forward cache: version choices may have changed on another page in this tab */
      loadChoices(ctx);
      ctx.rows.forEach(function (r) { renderRow(ctx, r); });
      renderModeControls(ctx);
    });
  }

  /* data-abff-placeholders="wide|narrow|tiny" (L): ≥1024 px, 360–1023 px, <360 px (interaction notes §8) */
  function initPlaceholders(ctx) {
    var inputs = qsa(ctx.root, '[data-abff-placeholders]');
    if (!inputs.length) return;
    var wide = mq('(min-width: 1024px)');
    var tiny = mq('(max-width: 359.98px)');
    function apply() {
      var k = wide && wide.matches ? 0 : (tiny && tiny.matches ? 2 : 1);
      inputs.forEach(function (i) {
        var parts = String(i.getAttribute('data-abff-placeholders') || '').split('|');
        var v = parts[k] || parts[1] || parts[0];
        if (v && i.getAttribute('placeholder') !== v) i.setAttribute('placeholder', v);
      });
    }
    onMqChange(wide, apply);
    onMqChange(tiny, apply);
    apply();
  }

  function tplFor(ctx, name) {
    return qs(ctx.root, 'template[data-abff-tpl="' + name + '"]') || document.querySelector('template[data-abff-tpl="' + name + '"]');
  }
  function cloneTpl(ctx, name) {
    var node = templateNode(tplFor(ctx, name));
    if (node) uniquifyIds(node, '-' + ctx.sid + '-' + (++ctx.rowSeq));
    return node;
  }

  /* ---------- header offset: --abff-header-h on the root (V3 header compacts on scroll) ---------- */
  function initMeasure(ctx) {
    var root = ctx.root;
    var sel = ctx.config.header_selector || '.abh-section';
    var ro = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(function () { schedule(); }) : null;
    var observed = null;
    var queued = false;
    function findHeader() {
      var h = null;
      try { h = document.querySelector(sel); } catch (e) { h = null; }
      return h || document.querySelector('[data-abh]') || document.querySelector('header');
    }
    function stickyNode(node) {
      for (var n = node; n && n !== document.documentElement && n !== document.body; n = n.parentElement) {
        var p = '';
        try { p = window.getComputedStyle(n).position; } catch (e) { p = ''; }
        if (p === 'sticky' || p === 'fixed') return n;
      }
      return null;
    }
    function measure() {
      var header = findHeader();
      var s = header ? stickyNode(header) || header : null;
      var h = s ? Math.max(0, Math.round(s.getBoundingClientRect().height)) : 0;
      if (h !== ctx.headerH || !root.style.getPropertyValue('--abff-header-h')) {
        ctx.headerH = h;
        root.style.setProperty('--abff-header-h', h + 'px');
      }
      if (ro && s !== observed) {
        if (observed) ro.unobserve(observed);
        if (s) ro.observe(s);
        observed = s;
      }
    }
    function schedule() {
      if (queued) return;
      queued = true;
      raf(function () { queued = false; measure(); });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    ctx.measure = measure;
    measure();
  }
  function scrollToEl(ctx, el, instant) {
    if (!el || !el.getBoundingClientRect) return;
    if (ctx.measure) ctx.measure();
    var top = Math.max(0, Math.round(el.getBoundingClientRect().top + scrollY() - (ctx.headerH || 0) - 16));
    try { window.scrollTo({ top: top, behavior: instant || reducedMotion() ? 'auto' : 'smooth' }); }
    catch (e) { try { window.scrollTo(0, top); } catch (e2) { /* jsdom */ } }
  }
  /* bring an element into view below the sticky header when it isn't */
  function revealEl(ctx, el) {
    if (!el || !el.getBoundingClientRect) return;
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || 0;
    if (!r.height && !r.top && !r.bottom) return; /* not laid out (jsdom) */
    if (r.top < (ctx.headerH || 0) || r.bottom > vh) scrollToEl(ctx, el, true);
  }

  /* ======================================================================================
   * Rows (finder results, part-number card, E1)
   * ==================================================================================== */
  function modelFrom(ctx) {
    if (ctx.context === 'e1') return ctx.root.getAttribute('data-from') || ctx.config.from || window.location.pathname;
    return core.fromFor(ctx.config.finder_path || window.location.pathname, ctx.route);
  }

  function loadChoices(ctx) {
    var m = ctx.model;
    ctx.choices = {};
    if (!m) return;
    var key = core.storageKey(ctx.config.storage_prefix, m.slug);
    var raw = core.readChoices(sessionStore(), key);
    var res = core.cleanChoices(raw, m, ctx.sold);
    ctx.choices = res.choices;
    if (res.changed) core.writeChoices(sessionStore(), key, ctx.choices);
  }
  function saveChoices(ctx) {
    if (!ctx.model) return;
    core.writeChoices(sessionStore(), core.storageKey(ctx.config.storage_prefix, ctx.model.slug), ctx.choices);
  }

  function rowOf(el) {
    var host = el && el.closest ? el.closest('[data-abff-row]') : null;
    return host && host.__abffRow ? host.__abffRow : null;
  }

  function buildRows(ctx, list, model) {
    ctx.rows = [];
    if (!list) return;
    while (list.firstChild) list.removeChild(list.firstChild);
    var n = model.stages.length;
    var lastComponent = null;
    model.stages.forEach(function (stage, i) {
      if (stage.component && stage.component !== lastComponent) {
        var head = cloneTpl(ctx, 'component');
        if (head) {
          fillSlots(head, 'label', stage.component);
          if (!qs(head, '[data-slot="label"]')) setText(head, stage.component);
          list.appendChild(head);
        }
        lastComponent = stage.component;
      }
      var row = buildRow(ctx, stage, core.eyebrowFor(model, stage, i, n, ctx.str), 'stage');
      if (row) { list.appendChild(row.node); ctx.rows.push(row); }
    });
    ctx.rows.forEach(function (r) { renderRow(ctx, r); });
  }

  /* The clone's root is the row, or wraps it (L's pn-card: status + old-label note around article[data-abff-row]).
     row.el = the row element (all §4 hooks); row.node = what gets inserted. */
  function buildRow(ctx, stage, eyebrow, kind, tplName) {
    var node = cloneTpl(ctx, tplName || 'row');
    if (!node) return null;
    var el = node.hasAttribute('data-abff-row') ? node : (qs(node, '[data-abff-row]') || node);
    var str = ctx.str;
    var row = { kind: kind, stage: stage, key: stage.key, el: el, node: node, qty: 1, included: false, unsure: false, open: false, error: null, busy: false, pendingCollapse: false, seq: ctx.rowSeq };
    el.__abffRow = row;
    el.setAttribute('data-abff-row', '');
    el.setAttribute('data-stage-key', stage.key);
    fillSlots(el, 'eyebrow', eyebrow);
    fillSlots(el, 'name', stage.name);
    qsa(el, 'input[data-abff-qty-input]').forEach(function (inp) {
      inp.value = '1';
      inp.setAttribute('aria-label', str('qty_aria', { name: stage.name }));
    });
    qsa(el, '[data-abff-add]').forEach(function (b) {
      if (b.tagName === 'BUTTON' && !b.getAttribute('type')) b.setAttribute('type', 'button');
      var lab = qs(b, '[data-slot="label"]');
      if (lab && !lab.textContent.trim()) setText(lab, str('add'));
    });
    var hint = qs(el, '[data-abff-add-hint]');
    if (hint) {
      ensureId(hint, 'abff-hint-' + ctx.sid + '-' + row.seq);
      if (!hint.textContent.trim()) setText(hint, str('choose_first'));
    }
    if (stage.versions.length > 1 && stage.status === 'confirmed') buildVersions(ctx, row);
    return row;
  }

  function buildVersions(ctx, row) {
    var str = ctx.str;
    var host = qs(row.el, '[data-abff-versions]');
    if (!host) return;
    var set = cloneTpl(ctx, 'versions');
    if (!set) return;
    var stage = row.stage;
    var role = core.lowerRole(core.roleOf(stage, str));
    fillSlots(set, 'hint', str('hint', { role: role }));
    var legend = qs(set, 'legend');
    if (legend && !legend.textContent.trim()) setText(legend, str('legend'));
    var cards = qs(set, '[data-abff-cards]') || set;
    var name = 'abff-v-' + ctx.sid + '-' + row.seq;
    var i = 0;
    function radioOf(card) { return qs(card, 'input[data-abff-vradio]') || qs(card, 'input[type="radio"]'); }
    stage.versions.forEach(function (v) {
      var card = cloneTpl(ctx, 'version-card');
      if (!card) return;
      var radio = radioOf(card);
      if (radio) {
        radio.setAttribute('data-abff-vradio', '');
        radio.type = 'radio';
        radio.name = name;
        radio.value = v.sku;
        ensureId(radio, name + '-' + (i++));
      }
      card.setAttribute('data-sku', v.sku);
      fillSlots(card, 'label', v.label);
      fillSlots(card, 'pn', v.sku);
      slotShow(card, 'condition', v.condition);
      var p = ctx.sold(v.sku);
      fillSlots(card, 'price', p ? ctx.money(p.price) : '');
      qsa(card, '[data-abff-vcard-notsold]').forEach(function (c) {
        if (!c.textContent.trim()) setText(c, str('not_sold'));
        setHidden(c, !!p);
      });
      setAttr(card, 'data-notsold', p ? null : 'true');
      cards.appendChild(card);
    });
    var unsure = cloneTpl(ctx, 'version-unsure');
    if (unsure) {
      var ur = radioOf(unsure);
      if (ur) {
        ur.setAttribute('data-abff-vradio', '');
        ur.type = 'radio';
        ur.name = name;
        ur.value = 'unsure';
        ensureId(ur, name + '-unsure');
      }
      unsure.setAttribute('data-unsure', 'true');
      qsa(unsure, '[data-slot="label"]').forEach(function (e) { if (!e.textContent.trim()) setText(e, str('unsure')); });
      qsa(unsure, '[data-slot="pn"], [data-slot="sub"]').forEach(function (e) { if (!e.textContent.trim()) setText(e, str('unsure_sub')); });
      cards.appendChild(unsure);
    }
    host.appendChild(set);
    row.versionsEl = set;
    row.howtoHost = qs(set, '[data-abff-howto]');
  }

  function fillHowto(ctx, row) {
    var host = row.howtoHost;
    if (!host) return;
    if (!host.__abffFilled) {
      var node = cloneTpl(ctx, 'howto');
      if (node) host.appendChild(node);
      host.__abffFilled = true;
    }
    var str = ctx.str;
    var role = core.lowerRole(core.roleOf(row.stage, str));
    var list = core.howtoList(row.stage, str);
    fillSlots(host, 'role', role);
    fillSlots(host, 'list', list);
    fillSlots(host, 'title', str('howto_title'));
    qsa(host, '[data-slot="body"]').forEach(function (b) { setText(b, str('howto_body', { role: role, list: list })); });
  }

  function renderRow(ctx, row) {
    var el = row.el;
    var str = ctx.str;
    /* row-level hooks only: never reach into the version cards / how-to (their slots belong to buildVersions) */
    var own = function (sel) { return ownAll(el, sel); };
    var stage = row.stage;
    var plan = core.rowPlan(stage, row.kind === 'stage' ? ctx.choices[row.key] : null, ctx.sold);
    row.plan = plan;
    var multiMode = ctx.mode === 'multi' && row.kind === 'stage' && ctx.context === 'finder';
    var locked = ctx.busy;
    var state = row.busy ? 'adding' : (row.error ? 'error' : plan.state);
    var p = plan.product;
    if (row.included && !plan.buyable) row.included = false;

    el.setAttribute('data-state', state);
    el.setAttribute('data-sku', plan.sku || '');
    el.setAttribute('data-included', row.included ? 'true' : 'false');
    setAttr(el, 'data-unsure', row.unsure ? 'true' : null);
    setAttr(el, 'data-unconfirmed', plan.unconfirmed ? 'true' : null);
    setAttr(el, 'data-chosen', plan.multi && plan.version ? 'true' : null);

    /* identity */
    slotShowIn(el, 'pn', plan.sku && !plan.unconfirmed ? str('pn', { pn: plan.sku }) : '');
    if (row.kind === 'stage') {
      slotShowIn(el, 'fit', plan.version && !plan.unconfirmed ? plan.version.condition : '');
      slotShowIn(el, 'also', plan.sku && !plan.unconfirmed ? core.alsoFitsText(ctx.index, plan.sku, ctx.model ? ctx.model.slug : null, str) : '');
    }
    /* phones: "Fits [Model]" short line (L: [data-slot="fits-short"]) */
    slotShowIn(el, 'fits-short', row.kind === 'stage' && plan.version && !plan.unconfirmed && ctx.model ? str('fits', { list: ctx.model.name }) : '');
    var imgProduct = p || (plan.multi && !plan.version ? standardProduct(ctx, stage) : null);
    setImageEls(own('img[data-slot="img"]'), imgProduct);
    own('a[data-abff-details]').forEach(function (a) {
      if (p) a.setAttribute('href', p.url);
      setHidden(a, !p);
    });

    /* price */
    var showPrice = !!p && plan.buyable;
    own('[data-slot="price"]').forEach(function (e) { setText(e, showPrice ? ctx.money(p.price) : ''); setHidden(e, !showPrice); });
    own('[data-slot="list"]').forEach(function (e) {
      var has = showPrice && p.compare_at != null;
      setText(e, has ? str('list_ref', { price: ctx.money(p.compare_at) }) : '');
      setHidden(e, !has);
    });
    own('[data-slot="unit"]').forEach(function (e) {
      setText(e, showPrice ? core.unitText(str, plan.version ? plan.version.label : '', stage.name) : '');
      setHidden(e, !showPrice);
    });
    hideAll(own('[data-abff-avail]'), !showPrice);
    hideAll(own('[data-abff-price]'), !showPrice && plan.state !== 'choose');

    /* version choice */
    var cardsOpen = plan.multi && !plan.unconfirmed && (!plan.version || row.unsure || row.open);
    own('[data-abff-versions-chip]').forEach(function (c) {
      fillSlots(c, 'nversions', str('nversions', { n: stage.versions.length }));
      setHidden(c, plan.state !== 'choose');
    });
    own('[data-abff-price-pending]').forEach(function (e) {
      if (!e.textContent.trim()) setText(e, str('price_pending'));
      setHidden(e, plan.state !== 'choose');
    });
    hideAll(own('[data-abff-versions]'), !cardsOpen);
    own('[data-abff-chosen]').forEach(function (c) {
      var show = plan.multi && !!plan.version && !cardsOpen;
      if (plan.version) {
        fillSlots(c, 'chosen', str('chosen', { label: plan.version.label }));
        fillSlots(c, 'chosen-short', str('chosen_short', { short: core.shortLabel(plan.version.label, stage.name), label: plan.version.label }));
        fillSlots(c, 'label', plan.version.label);
      }
      setHidden(c, !show);
      qsa(c, 'button[data-abff-change-version]').forEach(function (b) { b.disabled = locked; });
    });
    if (row.versionsEl) {
      qsa(row.versionsEl, 'input[data-abff-vradio]').forEach(function (r) {
        var on = r.value === 'unsure' ? row.unsure : (!!plan.version && r.value === plan.version.sku);
        r.checked = on;
        r.disabled = locked;
        var card = r.closest('[data-abff-vcard]') || r.parentNode;
        if (card && card !== row.versionsEl) setAttr(card, 'data-checked', on ? 'true' : null);
      });
      if (row.unsure) fillHowto(ctx, row);
      hideAll(qsa(row.versionsEl, '[data-abff-howto]'), !(row.unsure && cardsOpen));
    }

    /* buying */
    var purchasable = plan.buyable || plan.state === 'choose';
    var needsVersion = plan.state === 'choose';
    own('[data-abff-qty]').forEach(function (q) {
      setHidden(q, !purchasable);
      qsa(q, 'button, input').forEach(function (c) { c.disabled = needsVersion || locked; });
    });
    own('input[data-abff-qty-input]').forEach(function (inp) { if (String(inp.value) !== String(row.qty)) inp.value = String(row.qty); });
    var hint = own('[data-abff-add-hint]')[0] || null;
    own('[data-abff-add]').forEach(function (b) {
      setHidden(b, !purchasable || multiMode);
      var off = needsVersion || (locked && !row.busy);
      setAttr(b, 'aria-disabled', off || row.busy ? 'true' : null);
      setAttr(b, 'aria-busy', row.busy ? 'true' : null);
      setAttr(b, 'data-busy', row.busy ? '' : null);
      if (hint && needsVersion) b.setAttribute('aria-describedby', hint.id);
      else if (hint && b.getAttribute('aria-describedby') === hint.id) b.removeAttribute('aria-describedby');
      fillSlots(b, 'label', str(row.busy ? 'adding' : 'add'));
      hideAll(qsa(b, '[data-abff-spinner]'), !row.busy);
      if (!row.busy && b.style.minWidth) b.style.minWidth = '';
    });
    setHidden(hint, !(needsVersion && !multiMode));

    /* multi-select */
    own('[data-abff-include]').forEach(function (lab) {
      setHidden(lab, !(multiMode && purchasable));
      fillSlots(lab, 'include-text', str(needsVersion ? 'include_disabled' : 'include'));
      qsa(lab, 'input[data-abff-include-input]').forEach(function (cb) {
        cb.checked = !!row.included;
        cb.disabled = needsVersion || locked;
        cb.setAttribute('aria-label', needsVersion
          ? str('include_aria_choose', { name: stage.name })
          : str('include_aria', { name: stage.name, pn: plan.sku }));
      });
    });

    /* not sold online / not confirmed */
    own('[data-abff-notsold]').forEach(function (n) {
      setHidden(n, plan.state !== 'notsold');
      fillSlots(n, 'notsold-chip', str(plan.unconfirmed ? 'stage_unconfirmed' : 'not_sold'));
    });

    /* errors */
    own('[data-abff-row-error]').forEach(function (e) {
      fillSlots(e, 'error', row.error || '');
      setHidden(e, !row.error);
      setAttr(e, 'data-kind', row.error ? (row.errorKind || 'net') : null);
      qsa(e, '[data-abff-retry]').forEach(function (b) { setHidden(b, multiMode); b.disabled = locked; });
    });
    updateContacts(ctx, el);
  }

  function standardProduct(ctx, stage) {
    var std = null;
    stage.versions.forEach(function (v) { if (!std && v.standard) std = v; });
    return std ? ctx.sold(std.sku) : null;
  }
  function setImage(scope, slot, p) { setImageEls(qsa(scope, 'img[data-slot="' + slot + '"]'), p); }
  function setImageEls(imgs, p) {
    imgs.forEach(function (img) {
      var fb = img.parentNode ? qs(img.parentNode, '[data-abff-img-fallback]') : null;
      if (p && p.image) {
        if (img.getAttribute('src') !== p.image) img.setAttribute('src', p.image);
        img.setAttribute('alt', p.image_alt || '');
        setHidden(img, false);
        setHidden(fb, true);
      } else {
        img.removeAttribute('src');
        setHidden(img, true);
        setHidden(fb, false);
      }
    });
  }

  /* ---------- row events (delegated on the root) ---------- */
  function initRowEvents(ctx) {
    var root = ctx.root;
    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var row = rowOf(t);
      if (!row || !root.contains(row.el)) return;
      /* re-clicking the version that is already chosen (cards reopened with Change version) collapses them again */
      var vr = t.closest('input[data-abff-vradio]');
      if (vr) {
        if (row.open && vr.value !== 'unsure' && vr.value === ctx.choices[row.key] && !(row.arrowAt && Date.now() - row.arrowAt < 250)) chooseVersion(ctx, row, vr.value, false);
        return;
      }
      var btn = t.closest('[data-abff-add]');
      if (btn) { e.preventDefault(); addRow(ctx, row, btn); return; }
      var step = t.closest('[data-abff-dec], [data-abff-inc]');
      if (step) {
        e.preventDefault();
        if (step.disabled) return;
        var d = step.hasAttribute('data-abff-inc') ? 1 : -1;
        setQty(ctx, row, (core.clampQty(row.qty) || 1) + d);
        return;
      }
      var cv = t.closest('[data-abff-change-version]');
      if (cv) {
        e.preventDefault();
        if (ctx.busy) return;
        row.open = true;
        renderRow(ctx, row);
        var checked = qsa(row.versionsEl, 'input[data-abff-vradio]').filter(function (r) { return r.checked; })[0] || qs(row.versionsEl, 'input[data-abff-vradio]');
        focusEl(checked);
        return;
      }
      var retry = t.closest('[data-abff-retry]');
      if (retry) {
        e.preventDefault();
        var add = qsa(row.el, '[data-abff-add]').filter(function (b) { return !inHidden(b); })[0] || qs(row.el, '[data-abff-add]');
        addRow(ctx, row, add);
      }
    });
    root.addEventListener('keydown', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('input[data-abff-vradio]') && /^(Arrow|Up|Down|Left|Right)/.test(e.key)) {
        var row = rowOf(t);
        if (row) row.arrowAt = Date.now();
      }
      /* Enter on a version card confirms it: collapse like a click (orchestrator decision 4) */
      if (t.matches('input[data-abff-vradio]') && e.key === 'Enter') {
        e.preventDefault();
        var rv = rowOf(t);
        if (!rv) return;
        if (!t.checked && t.value !== 'unsure') t.checked = true;
        if (t.value === 'unsure') chooseVersion(ctx, rv, 'unsure', false);
        else chooseVersion(ctx, rv, t.value, false);
        return;
      }
      if (t.matches('input[data-abff-qty-input]') && e.key === 'Enter') {
        e.preventDefault();
        var r2 = rowOf(t);
        if (r2) setQty(ctx, r2, t.value, true);
      }
    });
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      var row = rowOf(t);
      if (!row) return;
      if (t.matches('input[data-abff-vradio]')) chooseVersion(ctx, row, t.value, row.arrowAt && Date.now() - row.arrowAt < 250);
      else if (t.matches('input[data-abff-include-input]')) {
        if (ctx.busy || !row.plan || !row.plan.buyable) { t.checked = false; return; }
        row.included = !!t.checked;
        clearBulkError(ctx);
        renderRow(ctx, row);
        renderSummaryBar(ctx, true);
      } else if (t.matches('input[data-abff-qty-input]')) setQty(ctx, row, t.value, true);
    });
    root.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('input[data-abff-qty-input]')) return;
      var row = rowOf(t);
      if (!row) return;
      var raw = String(t.value).trim();
      if (raw !== '' && parseInt(raw, 10) > 99) t.value = '99';
      var n = core.clampQty(t.value);
      if (n != null && n !== row.qty) {
        row.qty = n;
        if (row.included) renderSummaryBar(ctx, true);
      }
    });
    root.addEventListener('focusout', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('input[data-abff-qty-input]')) {
        var r = rowOf(t);
        if (r) setQty(ctx, r, t.value, true);
        return;
      }
      if (!t.matches('input[data-abff-vradio]')) return;
      var row = rowOf(t);
      if (!row || !row.pendingCollapse || !row.versionsEl) return;
      var next = e.relatedTarget;
      if (next && row.versionsEl.contains(next)) return;
      setTimeout(function () {
        if (!row.pendingCollapse) return;
        if (document.activeElement && row.versionsEl.contains(document.activeElement)) return;
        row.pendingCollapse = false;
        row.open = false;
        renderRow(ctx, row);
      }, 0);
    });
  }

  function setQty(ctx, row, value, normalizeInput) {
    var n = core.clampQty(value);
    if (n == null) n = row.qty || 1;
    var changed = n !== row.qty;
    row.qty = n;
    if (normalizeInput || changed) qsa(row.el, 'input[data-abff-qty-input]').forEach(function (inp) { if (String(inp.value) !== String(n)) inp.value = String(n); });
    if (changed && row.included) { clearBulkError(ctx); renderSummaryBar(ctx, true); }
  }

  /* Radios: a click or Space collapses the cards to "Version: … · Change version" and focuses Change version; an arrow
     key only selects (the customer is still moving through the cards) and the cards collapse when focus leaves them. */
  function chooseVersion(ctx, row, value, byArrow) {
    if (ctx.busy) return;
    if (value === 'unsure') {
      row.unsure = true;
      row.pendingCollapse = false;
      if (ctx.choices[row.key]) { delete ctx.choices[row.key]; saveChoices(ctx); }
      row.included = false;
      renderRow(ctx, row);
      renderSummaryBar(ctx, false);
      return;
    }
    var v = null;
    row.stage.versions.forEach(function (x) { if (x.sku === value) v = x; });
    if (!v) return;
    row.unsure = false;
    row.error = null;
    ctx.choices[row.key] = v.sku;
    saveChoices(ctx);
    if (byArrow) {
      row.open = true;
      row.pendingCollapse = true;
      renderRow(ctx, row);
    } else {
      row.open = false;
      row.pendingCollapse = false;
      renderRow(ctx, row);
      var cv = qsa(row.el, '[data-abff-chosen] button[data-abff-change-version]').filter(function (b) { return !inHidden(b); })[0];
      focusEl(cv || focusable(qs(row.el, '[data-abff-chosen]')));
    }
    clearBulkError(ctx);
    renderSummaryBar(ctx, row.included);
    renderModeControls(ctx);
  }

  /* ======================================================================================
   * Cart
   * ==================================================================================== */
  function cartUrl() {
    var r = '/';
    try { if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) r = String(window.Shopify.routes.root); } catch (e) { r = '/'; }
    return (r.charAt(r.length - 1) === '/' ? r : r + '/') + 'cart/add.js';
  }
  /* One POST /cart/add.js with items[]; 15 s timeout; requests never overlap (page-wide chain).
     Resolves { key, data }; rejects { kind: 'net' | 'timeout' | '422', message }. */
  function cartAdd(items) {
    var run = function () {
      return new Promise(function (resolve, reject) {
        if (typeof window.fetch !== 'function') { reject({ kind: 'net', message: '' }); return; }
        var done = false;
        var ctrl = null;
        try { ctrl = typeof window.AbortController === 'function' ? new window.AbortController() : null; } catch (e) { ctrl = null; }
        var timer = setTimeout(function () {
          if (done) return;
          done = true;
          try { if (ctrl) ctrl.abort(); } catch (e) { /* ignore */ }
          reject({ kind: 'timeout', message: '' });
        }, CART_TIMEOUT);
        var opts = {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: items })
        };
        if (ctrl) opts.signal = ctrl.signal;
        window.fetch(cartUrl(), opts).then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (data) { return { ok: r.ok, status: r.status, data: data || {} }; });
        }).then(function (res) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          if (!res.ok) {
            var msg = core.stripEndPeriod(res.data.description || res.data.message || '');
            reject({ kind: res.status === 422 && msg ? '422' : 'net', message: msg });
            return;
          }
          var first = (Array.isArray(res.data.items) && res.data.items[0]) || res.data;
          resolve({ key: first && first.key ? String(first.key) : null, data: res.data });
        }, function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          reject({ kind: 'net', message: '' });
        });
      });
    };
    var prev = cart.chain || Promise.resolve();
    var p = prev.then(run, run);
    cart.chain = p.then(function () { return null; }, function () { return null; });
    return p;
  }
  function afterAdd(ctx, key) {
    if (typeof window.openFlyoutCart === 'function') {
      try {
        var p = window.openFlyoutCart({ addedKey: key });
        if (p && typeof p.then === 'function') p.then(null, function () { ctx.announce(ctx.str('added_live')); });
        return 'drawer';
      } catch (e) { /* fall back */ }
    }
    var trig = document.querySelector('.flyout-trigger');
    if (trig) { trig.click(); return 'trigger'; }
    ctx.announce(ctx.str('added_live'));
    return 'none';
  }
  function setBusy(ctx, on) {
    ctx.busy = on;
    cart.busy = on;
    ctx.rows.concat(ctx.pnRow ? [ctx.pnRow] : []).forEach(function (r) { renderRow(ctx, r); });
    renderModeControls(ctx);
    renderSummaryBar(ctx, false);
  }
  function lockWidth(btn) {
    if (!btn || !btn.getBoundingClientRect) return;
    var w = Math.round(btn.getBoundingClientRect().width);
    if (w > 0) btn.style.minWidth = w + 'px';
  }

  function addRow(ctx, row, btn) {
    if (ctx.busy || cart.busy || row.busy) return;
    var plan = row.plan;
    if (!plan || !plan.buyable || !plan.product) return;
    if (btn && btn.getAttribute('aria-disabled') === 'true') return;
    var qty = core.clampQty(row.qty) || 1;
    lockWidth(btn);
    row.busy = true;
    row.error = null;
    setBusy(ctx, true);
    cartAdd([{ id: plan.product.variant_id, quantity: qty }]).then(function (res) {
      row.busy = false;
      row.qty = 1;
      setBusy(ctx, false);
      afterAdd(ctx, res.key);
    }, function (err) {
      row.busy = false;
      row.errorKind = err && err.kind === '422' ? '422' : 'net';
      row.error = row.errorKind === '422' ? ctx.str('row_error_422', { message: err.message }) : ctx.str('row_error_net');
      setBusy(ctx, false);
      var msg = qsa(row.el, '[data-abff-row-error]').filter(function (x) { return !inHidden(x); })[0];
      focusEl(focusable(msg));
    });
  }

  /* ======================================================================================
   * Purchase modes (finder results only)
   * ==================================================================================== */
  function renderModeControls(ctx) {
    var root = ctx.root;
    if (ctx.context !== 'finder') return;
    var multi = ctx.mode === 'multi';
    root.setAttribute('data-abff-mode', ctx.mode);
    var anyBuyable = ctx.rows.some(function (r) { return r.plan && r.plan.anySold; });
    hideAll(qsa(root, '[data-abff-modeline]'), multi || !anyBuyable);
    hideAll(qsa(root, '[data-abff-modebar]'), !multi);
    qsa(root, 'button[data-abff-mode-toggle]').forEach(function (b) {
      b.setAttribute('aria-pressed', multi ? 'true' : 'false');
      b.disabled = ctx.busy;
    });
    qsa(root, 'button[data-abff-mode-cancel]').forEach(function (b) { b.disabled = ctx.busy; });
  }
  function enterMulti(ctx) {
    if (ctx.busy || ctx.mode === 'multi') return;
    ctx.mode = 'multi';
    ctx.rows.forEach(function (r) { r.included = false; r.error = null; });
    clearBulkError(ctx);
    ctx.rows.forEach(function (r) { renderRow(ctx, r); });
    renderModeControls(ctx);
    renderSummaryBar(ctx, false);
    var cancel = qsa(ctx.root, 'button[data-abff-mode-cancel]').filter(function (b) { return !inHidden(b); })[0];
    focusEl(cancel);
    ctx.announce(ctx.str('mode_on'));
  }
  function exitMulti(ctx, opts) {
    opts = opts || {};
    if (ctx.mode !== 'multi') return;
    ctx.mode = 'single';
    ctx.rows.forEach(function (r) {
      r.included = false;
      if (opts.resetQty) r.qty = 1;
    });
    clearBulkError(ctx);
    ctx.rows.forEach(function (r) { renderRow(ctx, r); });
    renderModeControls(ctx);
    renderSummaryBar(ctx, false);
    if (opts.focus) {
      var t = qsa(ctx.root, 'button[data-abff-mode-toggle]').filter(function (b) { return !inHidden(b); })[0];
      focusEl(t);
    }
  }
  function selectionRows(ctx) {
    return ctx.rows.map(function (r) {
      var p = r.plan && r.plan.buyable ? r.plan.product : null;
      return { row: r, included: !!(r.included && p), qty: r.qty, price: p ? p.price : null, title: p ? p.title : '', sku: r.plan ? r.plan.sku : '', product: p };
    });
  }
  var announceTimer = null;
  function renderSummaryBar(ctx, announce) {
    var root = ctx.root;
    if (ctx.context !== 'finder') return;
    var bars = qsa(root, '[data-abff-summary-bar]');
    if (!bars.length) return;
    var str = ctx.str;
    var multi = ctx.mode === 'multi' && ctx.view === 'results';
    var sel = core.selection(selectionRows(ctx));
    var empty = sel.products === 0;
    var countTxt = core.countText(sel.products, sel.units, str);
    var amount = ctx.money(sel.subtotal);
    bars.forEach(function (bar) {
      setHidden(bar, !multi);
      setAttr(bar, 'data-empty', empty ? 'true' : null);
      qsa(bar, '[data-slot="count"]').forEach(function (e) { setText(e, countTxt); setHidden(e, empty); });
      qsa(bar, '[data-slot="subtotal"]').forEach(function (e) { setText(e, str('subtotal', { amount: amount })); setHidden(e, empty); });
      qsa(bar, '[data-slot="subtotal-phone"]').forEach(function (e) { setText(e, str('subtotal_phone', { amount: amount })); setHidden(e, empty); });
      var emptyEl = qs(bar, '[data-abff-bulk-empty]');
      if (emptyEl) {
        ensureId(emptyEl, 'abff-bulk-empty-' + ctx.sid);
        if (!emptyEl.textContent.trim()) setText(emptyEl, str('empty'));
        setHidden(emptyEl, !empty);
      }
      qsa(bar, 'button[data-abff-bulk-add]').forEach(function (b) {
        var label = ctx.bulkBusy ? str('adding') : (ctx.bulkError ? str('try_again') : str('bulk_add'));
        fillSlots(b, 'label', label);
        if (!qs(b, '[data-slot="label"]')) setText(b, label);
        hideAll(qsa(b, '[data-abff-spinner]'), !ctx.bulkBusy);
        setAttr(b, 'aria-disabled', empty || ctx.busy ? 'true' : null);
        setAttr(b, 'aria-busy', ctx.bulkBusy ? 'true' : null);
        setAttr(b, 'data-busy', ctx.bulkBusy ? '' : null);
        if (empty && emptyEl) b.setAttribute('aria-describedby', emptyEl.id);
        else if (emptyEl && b.getAttribute('aria-describedby') === emptyEl.id) b.removeAttribute('aria-describedby');
        if (!ctx.bulkBusy && b.style.minWidth) b.style.minWidth = '';
      });
      qsa(bar, '[data-abff-bulk-error]').forEach(function (e) {
        var slot = qs(e, '[data-slot="error"]');
        if (slot) setText(slot, ctx.bulkError || ''); else setText(e, ctx.bulkError || '');
        setHidden(e, !ctx.bulkError);
      });
    });
    if (announce && multi) {
      clearTimeout(announceTimer);
      announceTimer = setTimeout(function () {
        ctx.announce(empty ? str('empty') : countTxt + str('list_sep') + str('subtotal', { amount: amount }));
      }, 250);
    }
  }
  function clearBulkError(ctx) {
    if (!ctx.bulkError) return;
    ctx.bulkError = null;
    ctx.rows.forEach(function (r) { if (r.bulkErr) { r.error = null; r.bulkErr = false; renderRow(ctx, r); } });
  }
  function bulkAdd(ctx, btn) {
    if (ctx.busy || cart.busy || ctx.mode !== 'multi') return;
    var sel = selectionRows(ctx).filter(function (s) { return s.included; });
    if (!sel.length) return;
    var items = sel.map(function (s) { return { id: s.product.variant_id, quantity: core.clampQty(s.qty) || 1 }; });
    lockWidth(btn);
    ctx.rows.forEach(function (r) { if (r.bulkErr) { r.error = null; r.bulkErr = false; } });
    ctx.bulkError = null;
    ctx.bulkBusy = true;
    setBusy(ctx, true);
    cartAdd(items).then(function (res) {
      ctx.bulkBusy = false;
      ctx.busy = false;
      cart.busy = false;
      exitMulti(ctx, { resetQty: true, focus: true });
      setBusy(ctx, false);
      afterAdd(ctx, res.key);
    }, function (err) {
      ctx.bulkBusy = false;
      var is422 = err && err.kind === '422';
      ctx.bulkError = is422 ? ctx.str('bulk_error_422', { message: err.message + '.' }) : ctx.str('bulk_error_net');
      if (is422) {
        var hit = core.rowForMessage(err.message, sel);
        if (hit) { hit.row.error = ctx.str('row_error_422', { message: err.message }); hit.row.errorKind = '422'; hit.row.bulkErr = true; }
      }
      setBusy(ctx, false);
      var e = qsa(ctx.root, '[data-abff-bulk-error]').filter(function (x) { return !inHidden(x); })[0];
      focusEl(focusable(e));
    });
  }

  /* ======================================================================================
   * Contact links
   * ==================================================================================== */
  function contactParams(ctx, a) {
    if (a.getAttribute('data-abff-contact') === 'bare' || a.closest('[data-abff-avail-note]')) return {};
    var from = a.getAttribute('data-from') || modelFrom(ctx);
    var p = { from: from };
    var row = rowOf(a);
    var inNotsold = !!(a.closest('[data-abff-notsold]') || a.closest('[data-abff-row-error]'));
    var inHowto = !!a.closest('[data-abff-howto]');
    var model = ctx.model ? ctx.model.name : '';
    if (ctx.context === 'e1') {
      p.model = model;
      if (row && inNotsold && row.plan && !row.plan.unconfirmed) p.pn = row.plan.sku;
      return p;
    }
    var panel = a.closest('[data-abff-panel]');
    var view = panel ? panel.getAttribute('data-abff-panel') : ctx.view;
    if (view === 'results' || view === 'unconfirmed') {
      p.model = model;
      if (row && inNotsold && !inHowto && row.plan && !row.plan.unconfirmed) p.pn = row.plan.sku;
    } else if (view === 'pn') {
      p.pn = ctx.pnValue || '';
    }
    return p;
  }
  function updateContacts(ctx, scope) {
    var base = ctx.config.contact || '/pages/contact';
    var topic = ctx.config.topic || 'filters';
    qsa(scope || ctx.root, 'a[data-abff-contact]').forEach(function (a) {
      var href = core.contactHref(base, topic, contactParams(ctx, a));
      if (a.getAttribute('href') !== href) a.setAttribute('href', href);
    });
  }

  /* ======================================================================================
   * E1 (BOFA unit product page)
   * ==================================================================================== */
  function initE1(ctx) {
    var root = ctx.root;
    hideAll(qsa(root, '[data-abff-nojs]'), true);
    var m = ctx.index ? ctx.index.models[0] : null;
    if (!m) { updateContacts(ctx); return; }
    ctx.model = m;
    ctx.view = 'results';
    loadChoices(ctx);
    if (m.status === 'confirmed' && m.stages.length) buildRows(ctx, qs(root, 'ol[data-abff-rows]'), m);
    updateContacts(ctx);
  }

  /* ======================================================================================
   * Finder page
   * ==================================================================================== */
  function initFinder(ctx) {
    var root = ctx.root;
    ctx.h1 = qs(root, 'h1[data-abff-h1]');
    ctx.h1Default = ctx.h1 ? ctx.h1.textContent.replace(/\s+/g, ' ').trim() : '';
    qsa(root, '[data-abff-combobox]').forEach(function (box) { initCombobox(ctx, box); });
    initPnForms(ctx);
    initSheet(ctx);
    initBrowse(ctx);
    initNav(ctx);
    rewriteLinks(ctx, root);

    window.addEventListener('popstate', function (e) {
      if (!root.isConnected) return;
      if (ctx.ignorePop) { ctx.ignorePop = false; return; }
      var hadSheet = !!ctx.sheet;
      if (ctx.sheet) closeSheet(ctx, { fromPop: true });
      var route = core.parseRoute(window.location.search, window.location.hash);
      /* Back out of the phones model list: same page state, nothing to re-render */
      if (hadSheet && sameRoute(route, ctx.route)) return;
      renderRoute(ctx, route, { pop: true });
      var st = e.state;
      var y = st && st.abff && typeof st.scroll === 'number' ? st.scroll : null;
      if (y != null) raf(function () { try { window.scrollTo(0, y); } catch (err) { /* jsdom */ } });
    });

    if (!ctx.index) {
      showView(ctx, 'error');
      if (!qs(root, '[data-abff-panel="error"]')) showView(ctx, 'default');
      updateContacts(ctx);
      return;
    }
    /* L's pre-paint script may have set data-abff-view="loading" (CSS shows the skeleton) and put skeleton rows in the
       list; the real view replaces it now (rendering is synchronous from the inline islands). */
    qsa(root, 'ol[data-abff-rows]').forEach(function (ol) {
      qsa(ol, ':scope > *').forEach(function (li) { if (!li.hasAttribute('data-abff-row')) ol.removeChild(li); });
    });
    setAttr(root, 'aria-busy', null);
    /* Audit #5: a stale ?page=2 link (the old grid paged by 25) would page the products island (paginate by 250) to
       empty and every filter would read "Not sold online". Replace the URL without page (everything else kept, hash too)
       before rendering. Loop guard: the same target within 10 s renders as is. If the navigation doesn't happen, render
       after 1.5 s anyway. */
    var noPage = core.stripPageParam(window.location.search);
    if (noPage != null) {
      var target = window.location.pathname + noPage + window.location.hash;
      if (!recentPageFix(target)) {
        root.setAttribute('data-abff-redirect', target);
        markPageFix(target);
        try { window.location.replace(target); } catch (e) { /* sandboxed: fall through to the timer */ }
        setTimeout(function () { if (!ctx.route && root.isConnected) startFinder(ctx); }, 1500);
        return;
      }
    }
    startFinder(ctx);
  }

  var PAGE_FIX_KEY = 'abi-bofa-finder-page-fix';
  function recentPageFix(target) {
    try {
      var ss = sessionStore();
      var o = ss ? JSON.parse(ss.getItem(PAGE_FIX_KEY) || 'null') : null;
      return !!(o && o.t === target && Date.now() - o.at < 10000);
    } catch (e) { return false; }
  }
  function markPageFix(target) {
    try { var ss = sessionStore(); if (ss) ss.setItem(PAGE_FIX_KEY, JSON.stringify({ t: target, at: Date.now() })); } catch (e) { /* blocked */ }
  }

  function startFinder(ctx) {
    var root = ctx.root;
    var route = core.parseRoute(window.location.search, window.location.hash);
    var lm = route.kind === 'model' ? core.resolveModel(ctx.index, route.model) : null;
    if (lm) fillSlots(root, 'loading', ctx.str('loading', { model: lm.name }));
    renderRoute(ctx, route, { initial: true });
    if (route.where) {
      raf(function () { openWhere(ctx, false); });
      window.addEventListener('load', function () { openWhere(ctx, false); });
    }
  }

  function showView(ctx, view) {
    var root = ctx.root;
    ctx.view = view;
    root.setAttribute('data-abff-view', view);
    qsa(root, '[data-abff-panel]').forEach(function (p) { setHidden(p, p.getAttribute('data-abff-panel') !== view); });
    hideAll(qsa(root, '[data-abff-summary]').filter(function (s) { return !s.closest('[data-abff-panel]'); }), !(view === 'results' || view === 'unconfirmed'));
  }
  /* H1 text per view. Optional split for phones (copy deck: "[Model], with 'Filters for' for screen readers"): when the H1
     holds [data-slot="h1-prefix"] + [data-slot="h1-model"], the model view fills "Filters for " / "AD 350" separately and
     L can hide the prefix visually on phones; other views put the whole text in the prefix. */
  function setH1(ctx, t, modelName) {
    if (!ctx.h1) return;
    var pre = qs(ctx.h1, '[data-slot="h1-prefix"]');
    var mod = qs(ctx.h1, '[data-slot="h1-model"]');
    if (pre && mod) {
      var raw = ctx.str.raw('h1_model');
      var at = raw.indexOf('{model}');
      if (modelName != null && at !== -1) {
        setText(pre, raw.slice(0, at));
        setText(mod, modelName + raw.slice(at + 7));
      } else {
        setText(pre, t);
        setText(mod, '');
      }
      return;
    }
    var slot = qs(ctx.h1, '[data-slot="h1"]');
    if (slot) setText(slot, t); else setText(ctx.h1, t);
  }
  function setCrumb(ctx, t) {
    qsa(ctx.root, '[data-slot="crumb-last"]').forEach(function (el) {
      setText(el, t || '');
      setHidden(el, !t);
      setAttr(el, 'aria-current', t ? 'page' : null);
    });
    qsa(ctx.root, '[data-abff-crumb-last]').forEach(function (el) { setHidden(el, !t); });
  }
  function helpPrimary(ctx, panelName, on) {
    qsa(ctx.root, '[data-abff-panel="' + panelName + '"] [data-abff-help-panel]').forEach(function (h) { setAttr(h, 'data-abff-help-primary', on ? '' : null); });
  }

  /* Render a route. opts: initial (first load: aliases → replaceState), pop, push (history entry already written). */
  function renderRoute(ctx, route, opts) {
    opts = opts || {};
    var prevSlug = ctx.model ? ctx.model.slug : null;
    ctx.route = route;
    ctx.bulkError = null;
    ctx.pnRow = null;
    ctx.pnValue = '';
    closeChangePanels(ctx);
    if (route.kind === 'model') {
      var m = core.resolveModel(ctx.index, route.model);
      if (!m) renderNoModel(ctx, route.model);
      else {
        if (m.slug !== route.model) {
          route.model = m.slug;
          replaceUrl(ctx, route, window.location.hash);
        }
        if (m.slug !== prevSlug || opts.pop || opts.initial || ctx.view !== 'results') ctx.mode = 'single';
        if (m.status !== 'confirmed' || !m.stages.length) renderUnconfirmed(ctx, m);
        else renderResults(ctx, m);
      }
    } else if (route.kind === 'pn') renderPn(ctx, route.pn);
    else if (route.kind === 'all') renderAll(ctx, route.type);
    else renderDefault(ctx);
    if (route.kind !== 'model') { ctx.model = null; ctx.rows = []; ctx.mode = 'single'; }
    renderModeControls(ctx);
    renderSummaryBar(ctx, false);
    updateContacts(ctx);
  }

  /* the current history state (other scripts' keys kept) with this page's scroll position */
  function currentState() {
    var s = {};
    try {
      var cur = window.history.state;
      if (cur && typeof cur === 'object') Object.keys(cur).forEach(function (k) { s[k] = cur[k]; });
    } catch (e) { /* ignore */ }
    s.abff = 1;
    s.scroll = scrollY();
    return s;
  }
  function saveScroll() {
    try { window.history.replaceState(currentState(), ''); } catch (e) { /* sandboxed */ }
  }
  function replaceUrl(ctx, route, hash) {
    var href = core.routeHref(window.location.pathname, window.location.search, route, hash || '');
    try { window.history.replaceState(currentState(), '', href); } catch (e) { /* sandboxed */ }
  }
  function sameRoute(a, b) {
    if (!a || !b || a.kind !== b.kind) return false;
    if (a.kind === 'model') return a.model === b.model;
    if (a.kind === 'pn') return String(a.pn) === String(b.pn);
    if (a.kind === 'all') return a.type === b.type;
    return true;
  }
  /* Push a new state, render it, move focus. opts.focus: 'h1' | 'status' | element; opts.replace: replace instead of push */
  function navigate(ctx, route, opts) {
    opts = opts || {};
    var href = core.routeHref(window.location.pathname, window.location.search, route, '');
    try {
      saveScroll();
      /* the state already on screen (e.g. its own popular link) doesn't stack a duplicate history entry */
      if (opts.replace || sameRoute(route, ctx.route)) window.history.replaceState({ abff: 1, scroll: 0 }, '', href);
      else window.history.pushState({ abff: 1, scroll: 0 }, '', href);
    } catch (e) { /* sandboxed */ }
    renderRoute(ctx, route, { push: true });
    var target = null;
    if (opts.focus === 'h1') target = ctx.h1;
    else if (opts.focus === 'status') target = ctx.statusTarget || ctx.h1;
    else if (opts.focus && opts.focus.nodeType === 1) target = opts.focus;
    if (target) {
      focusEl(focusable(target));
      revealEl(ctx, ctx.h1 || target);
    }
  }

  function renderDefault(ctx) {
    showView(ctx, 'default');
    setH1(ctx, ctx.h1Default || ctx.str('h1_default'));
    setCrumb(ctx, '');
    qsa(ctx.root, '[data-abff-panel="default"] [data-abff-pn-status]').forEach(function (s) { setText(s, ''); });
  }

  /* "Donaldson BOFA · {app}": the map's per-model app (null → brand only); older maps without it fall back to the
     group label (never "Older models") */
  function appOf(ctx, m) {
    if (m.application) return m.application;
    if (m.group === 'older') return '';
    return ctx.index.groupLabel[m.group] || '';
  }
  function lifecycleKey(chip) {
    var c = core.norm(chip);
    if (!c) return null;
    if (c.indexOf('nolonger') !== -1 || c.indexOf('obsolete') !== -1) return 'lifecycle_phased';
    if (c.indexOf('phased') !== -1) return 'lifecycle_semi';
    return null;
  }
  function unitImage(ctx, m) {
    if (!m.unit_handle) return null;
    var u = ctx.products.units[m.unit_handle];
    if (u && u.image) return { image: String(u.image), image_alt: u.image_alt ? String(u.image_alt) : '' };
    var hit = null;
    ctx.products.list.forEach(function (p) { if (!hit && p.handle === m.unit_handle && p.image) hit = p; });
    return hit;
  }
  function fillSummary(ctx, m, unconfirmed) {
    var str = ctx.str;
    var app = appOf(ctx, m);
    var n = m.stages.length;
    qsa(ctx.root, '[data-abff-summary]').forEach(function (s) {
      setImage(s, 'model-img', unitImage(ctx, m));
      fillSlots(s, 'eyebrow', core.dropEmptyTail(str('eyebrow', { app: app })));
      fillSlots(s, 'model-name', m.name);
      if (unconfirmed) {
        fillSlots(s, 'count-line', str('unconfirmed_line'));
        fillSlots(s, 'count-line-phone', str('unconfirmed_line'));
      } else {
        fillSlots(s, 'count-line', str(n === 1 ? 'count_line_one' : 'count_line', { n: n }));
        fillSlots(s, 'count-line-phone', core.dropEmptyTail(str(n === 1 ? 'count_line_phone_one' : 'count_line_phone', { n: n, app: app })));
      }
      var lk = lifecycleKey(m.chip);
      qsa(s, '[data-abff-lifecycle]').forEach(function (l) {
        var t = lk ? str(lk) : '';
        var slot = qs(l, '[data-slot="lifecycle"]');
        if (slot) setText(slot, t); else setText(l, t);
        setHidden(l, !t);
      });
      qsa(s, 'button[data-abff-change-model]').forEach(function (b) { b.setAttribute('aria-label', str('change_model_aria', { model: m.name })); });
    });
  }

  function renderResults(ctx, m) {
    var root = ctx.root;
    var changed = !ctx.model || ctx.model.slug !== m.slug || ctx.view !== 'results';
    ctx.model = m;
    showView(ctx, 'results');
    setH1(ctx, ctx.str('h1_model', { model: m.name }), m.name);
    setCrumb(ctx, m.name);
    fillSummary(ctx, m, false);
    hideAll(qsa(root, '[data-abff-components-line]'), !core.isGrouped(m));
    helpPrimary(ctx, 'results', false);
    if (changed || !ctx.rows.length) {
      ctx.mode = 'single';
      loadChoices(ctx);
      buildRows(ctx, qs(qs(root, '[data-abff-panel="results"]') || root, 'ol[data-abff-rows]'), m);
    } else {
      ctx.rows.forEach(function (r) { renderRow(ctx, r); });
    }
    ctx.statusTarget = ctx.h1;
  }

  function renderUnconfirmed(ctx, m) {
    ctx.model = m;
    ctx.rows = [];
    showView(ctx, 'unconfirmed');
    setH1(ctx, ctx.str('h1_model', { model: m.name }), m.name);
    setCrumb(ctx, m.name);
    fillSummary(ctx, m, true);
    /* DOM-CONTRACT §6: h2 with [data-slot="model-name"]. The slot ON the h2 gets the sentence ("We’ll confirm the
       filters for your AD VL"); a slot inside the h2 (L writes the rest) or anywhere else gets the model name. */
    qsa(ctx.root, '[data-abff-panel="unconfirmed"] [data-slot="model-name"]').forEach(function (el) {
      if (el.closest('[data-abff-summary]')) return;
      setText(el, el.tagName === 'H2' ? ctx.str('unconfirmed_h2', { model: m.name }) : m.name);
    });
    ctx.statusTarget = ctx.h1;
  }

  function renderNoModel(ctx, typed) {
    var str = ctx.str;
    ctx.model = null;
    ctx.rows = [];
    showView(ctx, 'nomodel');
    setH1(ctx, ctx.h1Default || str('h1_default'));
    setCrumb(ctx, '');
    var panel = qs(ctx.root, '[data-abff-panel="nomodel"]');
    var typedText = String(typed == null ? '' : typed).trim();
    var statusEl = fillSentence(panel, 'typed', str('model_nomatch', { typed: typedText }), typedText);
    var ul = qs(panel, 'ul[data-abff-suggestions]');
    if (ul) {
      while (ul.firstChild) ul.removeChild(ul.firstChild);
      core.suggestModels(ctx.index, typed, 3).forEach(function (m) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.setAttribute('data-abff-model-link', '');
        a.setAttribute('data-model', m.slug);
        a.setAttribute('href', core.routeHref(window.location.pathname, window.location.search, { kind: 'model', model: m.slug }, ''));
        a.textContent = m.name;
        li.appendChild(a);
        ul.appendChild(li);
      });
      setHidden(ul, !ul.children.length);
    }
    /* "Did you mean one of these?" (L: [data-abff-suggest-lead]) only when there is something to choose */
    hideAll(qsa(panel, '[data-abff-suggest-lead]'), !(ul && ul.children.length));
    helpPrimary(ctx, 'nomodel', true);
    ctx.statusTarget = statusEl || ctx.h1;
  }

  /* ---------- part number ---------- */
  function initPnForms(ctx) {
    qsa(ctx.root, 'form[data-abff-pn-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = qs(form, 'input[data-abff-pn-input]');
        var v = input ? String(input.value || '').trim() : '';
        if (!v) { focusEl(input); return; }
        navigate(ctx, { kind: 'pn', pn: v }, { focus: 'status' });
      });
    });
  }
  function renderPn(ctx, typed) {
    var str = ctx.str;
    var root = ctx.root;
    showView(ctx, 'pn');
    setH1(ctx, str('h1_pn'));
    setCrumb(ctx, str('crumb_pn'));
    var raw = typed == null ? '' : String(typed);
    qsa(root, 'input[data-abff-pn-input]').forEach(function (i) { if (i.value !== raw) i.value = raw; });
    var panel = qs(root, '[data-abff-panel="pn"]');
    var result = qs(panel, '[data-abff-pn-result]');
    var nomatch = qs(panel, '[data-abff-pn-nomatch]');
    var status = qsa(panel, '[data-abff-pn-status]');
    if (result) while (result.firstChild) result.removeChild(result.firstChild);
    var n = core.normPn(raw);
    ctx.pnValue = n.pn;
    ctx.statusTarget = ctx.h1;
    if (!n.pn) {
      status.forEach(function (s) { setText(s, ''); });
      setHidden(nomatch, true);
      setHidden(result, true);
      helpPrimary(ctx, 'pn', false);
      return;
    }
    var f = core.findPn(ctx.index, ctx.products, n.pn);
    if (f) {
      ctx.pnValue = f.sku;
      var exact = str('pn_exact', { pn: f.sku });
      status.forEach(function (s) { setText(s, exact); });
      setHidden(nomatch, true);
      setHidden(result, false);
      helpPrimary(ctx, 'pn', false);
      buildPnCard(ctx, result, f, n);
      ctx.statusTarget = ctx.h1;
    } else {
      var msg = str('pn_nomatch', { typed: n.typed });
      status.forEach(function (s) { setText(s, msg); });
      setHidden(result, true);
      setHidden(nomatch, false);
      var typedEl = fillSentence(nomatch, 'typed', msg, n.typed);
      var dym = core.didYouMean(ctx.index, ctx.products, n.pn);
      qsa(nomatch, '[data-abff-dym]').forEach(function (d) {
        setHidden(d, !dym);
        if (!dym) return;
        fillSlots(d, 'dym-pn', dym.sku);
        fillSlots(d, 'dym-text', str('pn_dym', { pn: dym.sku }));
        var dymProduct = ctx.products.bySku[dym.sku] || null;
        fillSlots(d, 'dym-name', dym.title || (dymProduct ? dymProduct.title : '') || dym.role || '');
        setImage(d, 'dym-img', dymProduct);
        setImage(d, 'img', dymProduct);
        qsa(d, 'a[data-abff-dym-link]').forEach(function (a) {
          a.setAttribute('href', core.routeHref(window.location.pathname, window.location.search, { kind: 'pn', pn: dym.sku }, ''));
          a.setAttribute('data-pn', dym.sku);
          var lab = qs(a, '[data-slot="label"]');
          if (lab) setText(lab, str('dym_view', { pn: dym.sku }));
          else if (!qs(a, '[data-slot="dym-pn"]')) setText(a, str('dym_view', { pn: dym.sku }));
        });
      });
      helpPrimary(ctx, 'pn', true);
      ctx.statusTarget = typedEl || status[0] || ctx.h1;
    }
  }
  function buildPnCard(ctx, host, f, n) {
    if (!host) return;
    var str = ctx.str;
    var p = ctx.sold(f.sku);
    var role = core.chipRole(f.chip, str);
    var name = f.role || f.title || (p ? p.title : '') || f.sku;
    var stage = { key: 'pn', name: name, family: '', status: 'confirmed', component: null, versions: [{ sku: f.sku, label: f.role || name, condition: '', standard: true, handle: f.handle }] };
    var row = buildRow(ctx, stage, role ? str('pn_eyebrow', { role: role }) : str('pn_eyebrow_plain'), 'pn', 'pn-card');
    if (!row) return;
    var el = row.el;
    var node = row.node;
    fillSlots(node, 'status', str('pn_exact', { pn: f.sku }));
    qsa(node, '[data-abff-oldlabel]').forEach(function (o) {
      var t = n.had1U ? '' : str('pn_oldlabel', { old: core.oldLabel(f.sku) });
      var slot = qs(o, '[data-slot="oldlabel"]');
      if (slot) setText(slot, t); else setText(o, t);
      setHidden(o, !t);
    });
    qsa(el, 'ul[data-slot="fits"]').forEach(function (ul) {
      while (ul.firstChild) ul.removeChild(ul.firstChild);
      var seen = {};
      f.fits.forEach(function (x) {
        if (seen[x.model]) return;
        seen[x.model] = true;
        var li = document.createElement('li');
        var m = ctx.index ? ctx.index.bySlug[x.model] : null;
        var host = li;
        if (m) {
          host = document.createElement('a');
          host.setAttribute('data-abff-model-link', '');
          host.setAttribute('data-model', m.slug);
          host.setAttribute('href', core.routeHref(window.location.pathname, window.location.search, { kind: 'model', model: m.slug }, ''));
          li.appendChild(host);
        }
        var nm = document.createElement('span');
        nm.setAttribute('data-slot', 'name');
        nm.textContent = (x.name || x.model) + (x.conditional && x.condition ? str('list_sep') + x.condition : '');
        host.appendChild(nm);
        var chipText = m ? (m.status !== 'confirmed' ? str('chip_unconfirmed') : (m.chip || '')) : '';
        if (chipText) {
          var chip = document.createElement('span');
          chip.setAttribute('data-slot', 'chip');
          chip.textContent = chipText;
          host.appendChild(chip);
        }
        ul.appendChild(li);
      });
      if (!f.fits.length) {
        var li2 = document.createElement('li');
        li2.setAttribute('data-abff-unconfirmed', '');
        li2.textContent = str('pn_fit_unconfirmed');
        ul.appendChild(li2);
      }
    });
    host.appendChild(node);
    ctx.pnRow = row;
    renderRow(ctx, row);
  }

  /* ---------- Browse all ---------- */
  function initBrowse(ctx) {
    var root = ctx.root;
    root.addEventListener('click', function (e) {
      var chip = e.target && e.target.closest ? e.target.closest('button[data-abff-type]') : null;
      if (chip && root.contains(chip)) {
        e.preventDefault();
        var t = chip.getAttribute('data-abff-type');
        if (core.TYPES.indexOf(t) === -1 || (ctx.route && ctx.route.kind === 'all' && ctx.route.type === t)) return;
        var route = { kind: 'all', type: t };
        ctx.route = route;
        replaceUrl(ctx, route, '');
        ctx.browse.type = t;
        ctx.browse.shown = core.PAGE_SIZE;
        renderBrowseList(ctx, true);
        updateContacts(ctx);
        return;
      }
      var more = e.target && e.target.closest ? e.target.closest('button[data-abff-more]') : null;
      if (more && root.contains(more)) {
        e.preventDefault();
        var before = ctx.browse.shown;
        ctx.browse.shown += core.PAGE_SIZE;
        renderBrowseList(ctx, true, before);
      }
    });
    qsa(root, 'select[data-abff-sort]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        ctx.browse.sort = sel.value === 'price' ? 'price' : 'pn';
        ctx.browse.shown = core.PAGE_SIZE;
        renderBrowseList(ctx, true);
      });
    });
  }
  function renderAll(ctx, type) {
    showView(ctx, 'all');
    setH1(ctx, ctx.str('h1_all'));
    setCrumb(ctx, ctx.str('crumb_all'));
    if (!ctx.browse.rows) ctx.browse.rows = core.browseRows(ctx.index, ctx.products);
    ctx.browse.type = type || 'all';
    ctx.browse.shown = core.PAGE_SIZE;
    var sel = qs(ctx.root, 'select[data-abff-sort]');
    if (sel) ctx.browse.sort = sel.value === 'price' ? 'price' : 'pn';
    renderBrowseList(ctx, false);
    ctx.statusTarget = ctx.h1;
  }
  function renderBrowseList(ctx, announce, appendFrom) {
    var root = ctx.root;
    var str = ctx.str;
    var b = ctx.browse;
    var rows = b.rows || [];
    var counts = core.typeCounts(rows);
    var pressed = null;
    qsa(root, 'button[data-abff-type]').forEach(function (chip) {
      var t = chip.getAttribute('data-abff-type');
      chip.setAttribute('aria-pressed', t === b.type ? 'true' : 'false');
      fillSlots(chip, 'count', counts[t] != null ? String(counts[t]) : '0');
      if (t === b.type && !inHidden(chip)) pressed = chip;
    });
    if (pressed && appendFrom == null && pressed.scrollIntoView) {
      try { pressed.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' }); } catch (e) { /* old engines */ }
    }
    var list = core.browseList(rows, b.type, b.sort);
    var shown = Math.min(b.shown, list.length);
    var ul = qs(root, 'ul[data-abff-all-list]');
    var firstNew = null;
    if (ul) {
      if (appendFrom == null) while (ul.firstChild) ul.removeChild(ul.firstChild);
      var start = appendFrom == null ? 0 : Math.min(appendFrom, ul.children.length);
      for (var i = start; i < shown; i++) {
        var node = buildAllRow(ctx, list[i]);
        if (!node) break;
        ul.appendChild(node);
        if (!firstNew) firstNew = node;
      }
    }
    var status = core.browseStatus(shown, list.length, b.type, str);
    qsa(root, '[data-abff-all-status]').forEach(function (s) { setText(s, status); });
    qsa(root, 'button[data-abff-more]').forEach(function (m) {
      var rest = list.length - shown;
      var n = Math.min(core.PAGE_SIZE, rest);
      var lab = qs(m, '[data-slot="label"]');
      if (lab) setText(lab, str('more', { n: n })); else setText(m, str('more', { n: n }));
      setHidden(m, rest <= 0);
    });
    if (appendFrom != null && firstNew) {
      var link = qs(firstNew, 'a[data-abff-details]') || qs(firstNew, 'a');
      focusEl(link || focusable(firstNew));
    } else if (announce) ctx.announce(status);
  }
  function buildAllRow(ctx, r) {
    var node = cloneTpl(ctx, 'all-row');
    if (!node) return null;
    var str = ctx.str;
    var p = r.product;
    node.setAttribute('data-sku', r.sku);
    node.setAttribute('data-type', r.type);
    setImage(node, 'img', p);
    fillSlots(node, 'title', p.title);
    fillSlots(node, 'pn', str('pn', { pn: r.sku }));
    qsa(node, '[data-slot="fits"]').forEach(function (f) {
      if (r.fits.length) { setText(f, str('fits', { list: core.listText(r.fits, 4, str) })); f.removeAttribute('data-abff-unconfirmed'); }
      else { setText(f, str('fit_unconfirmed')); f.setAttribute('data-abff-unconfirmed', ''); }
    });
    fillSlots(node, 'price', ctx.money(p.price));
    qsa(node, '[data-slot="list"]').forEach(function (e) { setText(e, p.compare_at != null ? str('list_ref', { price: ctx.money(p.compare_at) }) : ''); setHidden(e, p.compare_at == null); });
    qsa(node, '[data-slot="unit"]').forEach(function (e) { setText(e, core.unitText(str, r.role, p.title)); });
    qsa(node, 'a[data-abff-details]').forEach(function (a) { a.setAttribute('href', p.url); });
    return node;
  }

  /* ---------- model combobox (ARIA 1.2) ---------- */
  function initCombobox(ctx, box) {
    var str = ctx.str;
    var input = qs(box, 'input[data-abff-model-input]');
    var toggle = qs(box, 'button[data-abff-model-toggle]');
    var list = qs(box, '[data-abff-listbox]');
    var count = qs(box, '[data-abff-model-count]');
    if (!input || !list) return;
    var n = ++boxSeq;
    var listId = ensureId(list, 'abff-lb-' + ctx.sid + '-' + n);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-controls', listId);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    if (!list.getAttribute('role')) list.setAttribute('role', 'listbox');
    var st = { open: false, all: false, results: [], active: -1, box: box, input: input };
    ctx.boxes.push(st);
    var countTimer = null;

    function listShown() { return !list.hasAttribute('hidden'); }
    function optId(i) { return listId + '-o' + i; }
    function buildGroup(group) {
      var li = document.createElement('li');
      li.setAttribute('role', 'group');
      li.setAttribute('data-abff-optgroup', group);
      var label = document.createElement('span');
      label.setAttribute('role', 'presentation');
      label.setAttribute('data-slot', 'label');
      label.id = listId + '-g-' + group;
      label.textContent = str(group === 'older' ? 'group_older' : 'group_current');
      li.setAttribute('aria-labelledby', label.id);
      var ul = document.createElement('ul');
      ul.setAttribute('role', 'none');
      li.appendChild(label);
      li.appendChild(ul);
      return { li: li, ul: ul };
    }
    function buildOption(r, i) {
      var li = document.createElement('li');
      li.id = optId(i);
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.setAttribute('data-abff-option', '');
      li.setAttribute('data-index', String(i));
      li.setAttribute('data-model', r.model.slug);
      var name = document.createElement('span');
      name.setAttribute('data-slot', 'name');
      name.textContent = r.model.name;
      li.appendChild(name);
      var chipText = r.model.status !== 'confirmed' ? str('chip_unconfirmed') : (r.model.chip || '');
      if (chipText) {
        var chip = document.createElement('span');
        chip.setAttribute('data-slot', 'chip');
        chip.textContent = chipText;
        li.appendChild(chip);
      }
      return li;
    }
    function updateActive() {
      var opts = qsa(list, '[role="option"]');
      opts.forEach(function (li, i) { li.setAttribute('aria-selected', i === st.active ? 'true' : 'false'); });
      var cur = st.active >= 0 ? opts[st.active] : null;
      if (cur && listShown()) {
        input.setAttribute('aria-activedescendant', cur.id);
        if (cur.scrollIntoView) { try { cur.scrollIntoView({ block: 'nearest' }); } catch (e) { /* old engines */ } }
      } else input.removeAttribute('aria-activedescendant');
    }
    function setCount(t) {
      if (!count) return;
      clearTimeout(countTimer);
      countTimer = setTimeout(function () { setText(count, t); }, 250);
    }
    function render() {
      var q = input.value;
      var typed = core.norm(q).length > 0;
      /* Alt+↓ and the chevron open the FULL list (interaction notes §4), even with text in the field */
      st.results = st.open ? core.searchModels(ctx.index, st.all ? '' : q) : [];
      if (st.active >= st.results.length) st.active = -1;
      while (list.firstChild) list.removeChild(list.firstChild);
      var last = null;
      var into = list;
      st.results.forEach(function (r, i) {
        if (r.group !== last) {
          var g = buildGroup(r.group);
          list.appendChild(g.li);
          into = g.ul;
          last = r.group;
        }
        into.appendChild(buildOption(r, i));
      });
      var show = st.open && st.results.length > 0;
      setHidden(list, !show);
      input.setAttribute('aria-expanded', show ? 'true' : 'false');
      if (toggle) toggle.setAttribute('aria-expanded', show ? 'true' : 'false');
      if (!st.open) setCount('');
      else if (!st.results.length) setCount(typed ? str('combobox_none', { typed: q.trim() }) : '');
      else setCount(st.results.length === 1 ? str('combobox_count_one') : str('combobox_count', { n: st.results.length }));
      updateActive();
    }
    st.close = function () { st.open = false; st.all = false; st.active = -1; render(); };
    st.reset = function () { input.value = ''; st.close(); };
    function choose(m) {
      if (!m) return;
      st.close();
      input.value = '';
      var fromSheet = !!ctx.sheet;
      if (ctx.sheet) closeSheet(ctx, { silent: true });
      navigate(ctx, { kind: 'model', model: m.slug }, { focus: 'h1', replace: fromSheet });
    }
    function openList(all, activeLast) {
      st.open = true;
      st.all = !!all;
      render();
      if (activeLast != null) { st.active = st.results.length ? (activeLast ? st.results.length - 1 : 0) : -1; updateActive(); }
    }
    input.addEventListener('input', function () {
      st.all = false;
      st.open = core.norm(input.value).length > 0;
      st.active = -1;
      render();
    });
    input.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowDown' || k === 'Down') {
        e.preventDefault();
        if (e.altKey) { openList(true, null); return; }
        if (!listShown()) { openList(!core.norm(input.value), false); return; }
        st.active = st.active < 0 ? 0 : Math.min(st.active + 1, st.results.length - 1);
        updateActive();
      } else if (k === 'ArrowUp' || k === 'Up') {
        e.preventDefault();
        if (e.altKey) return;
        if (!listShown()) { openList(!core.norm(input.value), true); return; }
        st.active = st.active <= 0 ? 0 : st.active - 1;
        updateActive();
      } else if (k === 'Enter') {
        e.preventDefault();
        if (listShown() && st.active >= 0 && st.results[st.active]) { choose(st.results[st.active].model); return; }
        var q = input.value.trim();
        if (!core.norm(q)) return;
        var exact = core.resolveModel(ctx.index, q);
        if (exact) { choose(exact); return; }
        var res = core.searchModels(ctx.index, q);
        if (res.length === 1) { choose(res[0].model); return; }
        if (!res.length) {
          st.close();
          input.value = '';
          if (ctx.sheet) closeSheet(ctx, { silent: true });
          navigate(ctx, { kind: 'model', model: q }, { focus: 'status' });
          return;
        }
        openList(false, null);
      } else if (k === 'Escape' || k === 'Esc') {
        if (listShown()) { e.preventDefault(); e.stopPropagation(); st.close(); return; }
        var panel = box.closest('[data-abff-change-panel]');
        if (panel) { e.preventDefault(); closeChangePanels(ctx, true); }
      } else if (k === 'Tab') {
        if (st.open) st.close();
      }
    });
    input.addEventListener('blur', function () {
      setTimeout(function () {
        if (document.activeElement === input || (toggle && document.activeElement === toggle)) return;
        if (st.open) st.close();
      }, 0);
    });
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    list.addEventListener('click', function (e) {
      var li = e.target && e.target.closest ? e.target.closest('[role="option"]') : null;
      if (!li) return;
      var r = st.results[parseInt(li.getAttribute('data-index'), 10)];
      if (r) choose(r.model);
    });
    list.addEventListener('mousemove', function (e) {
      var li = e.target && e.target.closest ? e.target.closest('[role="option"]') : null;
      if (!li) return;
      var i = parseInt(li.getAttribute('data-index'), 10);
      if (!isNaN(i) && i !== st.active) { st.active = i; updateActive(); }
    });
    if (toggle) {
      if (!toggle.getAttribute('type')) toggle.setAttribute('type', 'button');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('mousedown', function (e) { if (listShown()) e.preventDefault(); });
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        if (isPhone() && qs(ctx.root, '[data-abff-sheet]')) { st.close(); openSheet(ctx, toggle); return; }
        if (listShown()) { st.close(); focusEl(input); return; }
        openList(true, null);
        focusEl(input);
      });
    }
    render();
  }

  /* ---------- phones: full-screen model list ---------- */
  function initSheet(ctx) {
    var root = ctx.root;
    var sheet = qs(root, '[data-abff-sheet]');
    if (!sheet) return;
    var filter = qs(sheet, 'input[data-abff-sheet-filter]');
    qsa(sheet, 'button[data-abff-sheet-close]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); closeSheet(ctx); });
    });
    sheet.addEventListener('keydown', function (e) {
      if (!ctx.sheet) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeSheet(ctx); return; }
      if (e.key !== 'Tab') return;
      var f = qsa(sheet, 'a[href], button, input, select, textarea, [tabindex]').filter(function (el) {
        return !el.disabled && el.getAttribute('tabindex') !== '-1' && !inHidden(el);
      });
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); focusEl(last); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); focusEl(first); }
    });
    if (filter) {
      filter.addEventListener('input', function () { filterSheet(ctx, filter.value, true); });
      filter.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
    }
  }
  function filterSheet(ctx, value, announce) {
    var sheet = qs(ctx.root, '[data-abff-sheet]');
    if (!sheet) return;
    var qn = core.norm(value);
    var qnAlt = qn.replace(/^advantage(ad)?/, 'ad');
    var visible = 0;
    var links = qsa(sheet, 'a[data-abff-model-link]');
    links.forEach(function (a) {
      var m = ctx.index ? ctx.index.bySlug[a.getAttribute('data-model')] : null;
      var keys = m ? [core.norm(m.name), core.norm(m.slug)].concat(m.aliases) : [core.norm(a.textContent)];
      var hit = !qn || keys.some(function (k) { return k.indexOf(qn) !== -1 || (qnAlt && k.indexOf(qnAlt) !== -1); });
      var host = a.closest('li') || a;
      setHidden(host, !hit);
      if (hit) visible += 1;
    });
    /* an entry counts by its own hidden flag (the group, or the closed sheet, may be hidden itself) */
    var shownEntry = function (a) { return !(a.closest('li') || a).hasAttribute('hidden'); };
    var groups = qsa(sheet, '[data-abff-dir-group]');
    if (groups.length) {
      groups.forEach(function (g) {
        setHidden(g, !qsa(g, 'a[data-abff-model-link]').some(shownEntry));
      });
    } else {
      qsa(sheet, 'ul, ol').forEach(function (l) {
        var ls = qsa(l, 'a[data-abff-model-link]');
        if (!ls.length) return;
        var any = ls.some(shownEntry);
        setHidden(l, !any);
        var h = l.previousElementSibling;
        if (h && /^H[2-6]$/.test(h.tagName)) setHidden(h, !any);
      });
    }
    qsa(sheet, '[data-abff-sheet-empty]').forEach(function (e) { setHidden(e, visible > 0); });
    if (announce) ctx.announce(visible === 1 ? ctx.str('sheet_count_one') : ctx.str('sheet_count', { n: visible }));
  }
  function openSheet(ctx, opener) {
    var sheet = qs(ctx.root, '[data-abff-sheet]');
    if (!sheet) return false;
    if (ctx.sheet) return true;
    ctx.sheet = { opener: opener || document.activeElement, pushed: false };
    var filter = qs(sheet, 'input[data-abff-sheet-filter]');
    if (filter) filter.value = '';
    filterSheet(ctx, '', false);
    setHidden(sheet, false);
    ctx.root.setAttribute('data-abff-sheet-open', '');
    if (document.documentElement) document.documentElement.setAttribute('data-abff-sheet-open', '');
    try {
      saveScroll();
      window.history.pushState({ abff: 1, abffSheet: 1, scroll: scrollY() }, '', window.location.pathname + window.location.search + '#all-models');
      ctx.sheet.pushed = true;
    } catch (e) { /* sandboxed */ }
    setTimeout(function () { focusEl(filter || focusable(sheet)); }, 0);
    return true;
  }
  /* opts.fromPop: history already moved; opts.silent: a model is being chosen (its entry replaces the sheet's) */
  function closeSheet(ctx, opts) {
    opts = opts || {};
    var s = ctx.sheet;
    if (!s) return;
    ctx.sheet = null;
    setHidden(qs(ctx.root, '[data-abff-sheet]'), true);
    ctx.root.removeAttribute('data-abff-sheet-open');
    if (document.documentElement) document.documentElement.removeAttribute('data-abff-sheet-open');
    if (opts.silent) return;
    if (!opts.fromPop && s.pushed && window.history.state && window.history.state.abffSheet) {
      ctx.ignorePop = true;
      try { window.history.back(); } catch (e) { ctx.ignorePop = false; }
    }
    if (s.opener && !inHidden(s.opener)) focusEl(s.opener);
  }

  /* ---------- links and buttons that change state ---------- */
  function rewriteLinks(ctx, scope) {
    var path = window.location.pathname;
    var search = window.location.search;
    qsa(scope, 'a[data-abff-model-link]').forEach(function (a) {
      var slug = a.getAttribute('data-model');
      if (slug) a.setAttribute('href', core.routeHref(path, search, { kind: 'model', model: slug }, ''));
    });
    qsa(scope, 'a[data-abff-browse]').forEach(function (a) { a.setAttribute('href', core.routeHref(path, search, { kind: 'all', type: 'all' }, '')); });
    qsa(scope, 'a[data-abff-to-pn]').forEach(function (a) { a.setAttribute('href', core.routeHref(path, search, { kind: 'pn', pn: '' }, '')); });
    qsa(scope, 'a[data-abff-to-model]').forEach(function (a) { a.setAttribute('href', core.routeHref(path, search, { kind: 'default' }, '')); });
  }
  function firstVisible(ctx, sel) {
    return qsa(ctx.root, sel).filter(function (el) { return !inHidden(el); })[0] || null;
  }
  function closeChangePanels(ctx, focusBack) {
    var any = false;
    qsa(ctx.root, '[data-abff-change-panel]').forEach(function (p) {
      if (!p.hasAttribute('hidden')) any = true;
      setHidden(p, true);
    });
    qsa(ctx.root, 'button[data-abff-change-model]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    ctx.boxes.forEach(function (b) { if (b.box.closest('[data-abff-change-panel]')) b.reset(); });
    if (any && focusBack) focusEl(firstVisible(ctx, '[data-abff-summary] button[data-abff-change-model]') || firstVisible(ctx, 'button[data-abff-change-model]'));
  }
  function openChangePanel(ctx, btn) {
    var panel = qs(ctx.root, '[data-abff-change-panel]');
    if (!panel) {
      var input = firstVisible(ctx, 'input[data-abff-model-input]');
      if (input) focusEl(input);
      return;
    }
    var inSummary = !!(btn && btn.closest('[data-abff-summary]'));
    /* the summary's "Change model" toggles the panel; the tail's "Choose another model" only opens it */
    if (!panel.hasAttribute('hidden') && inSummary) { closeChangePanels(ctx, true); return; }
    setHidden(panel, false);
    qsa(ctx.root, 'button[data-abff-change-model]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    if (!inSummary) revealEl(ctx, panel);
    focusEl(qs(panel, 'input[data-abff-model-input]'));
  }
  function openWhere(ctx, focus) {
    var d = firstVisible(ctx, 'details[data-abff-where]');
    if (!d) return;
    d.open = true;
    d.setAttribute('open', '');
    scrollToEl(ctx, d, true);
    if (focus) focusEl(qs(d, 'summary'));
  }
  function initNav(ctx) {
    var root = ctx.root;
    root.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      var t = e.target && e.target.closest ? e.target : null;
      if (!t) return;
      var a = t.closest('a');
      var modifier = e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
      if (a && root.contains(a) && !modifier) {
        if (a.hasAttribute('data-abff-model-link')) {
          var slug = a.getAttribute('data-model');
          if (!slug) return;
          e.preventDefault();
          var fromSheet = !!(ctx.sheet && a.closest('[data-abff-sheet]'));
          if (fromSheet) closeSheet(ctx, { silent: true });
          navigate(ctx, { kind: 'model', model: slug }, { focus: 'h1', replace: fromSheet });
          return;
        }
        if (a.hasAttribute('data-abff-browse')) { e.preventDefault(); navigate(ctx, { kind: 'all', type: 'all' }, { focus: 'h1' }); return; }
        if (a.hasAttribute('data-abff-to-pn')) {
          e.preventDefault();
          navigate(ctx, { kind: 'pn', pn: '' }, {});
          var pi = firstVisible(ctx, '[data-abff-panel="pn"] input[data-abff-pn-input]') || firstVisible(ctx, 'input[data-abff-pn-input]');
          focusEl(pi);
          revealEl(ctx, ctx.h1);
          return;
        }
        if (a.hasAttribute('data-abff-to-model')) {
          e.preventDefault();
          navigate(ctx, { kind: 'default' }, {});
          focusEl(firstVisible(ctx, '[data-abff-panel="default"] input[data-abff-model-input]') || firstVisible(ctx, 'input[data-abff-model-input]'));
          revealEl(ctx, ctx.h1);
          return;
        }
        if (a.hasAttribute('data-abff-dym-link')) {
          var pn = a.getAttribute('data-pn');
          if (!pn) return;
          e.preventDefault();
          navigate(ctx, { kind: 'pn', pn: pn }, { focus: 'h1' });
          return;
        }
        if (a.hasAttribute('data-abff-open-where')) {
          e.preventDefault();
          openWhere(ctx, true);
          try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + '#where-to-find'); } catch (err) { /* sandboxed */ }
          return;
        }
        if (a.hasAttribute('data-abff-all-models')) {
          e.preventDefault();
          if (ctx.view !== 'default') navigate(ctx, { kind: 'default' }, {});
          var dir = firstVisible(ctx, 'section[data-abff-directory]');
          if (dir) { scrollToEl(ctx, dir, false); focusEl(focusable(qs(dir, 'h2') || dir)); }
          return;
        }
      }
      var b = t.closest('button');
      if (!b || !root.contains(b)) return;
      if (b.hasAttribute('data-abff-open-sheet')) {
        e.preventDefault();
        if (!openSheet(ctx, b)) {
          if (ctx.view !== 'default') navigate(ctx, { kind: 'default' }, {});
          var d2 = firstVisible(ctx, 'section[data-abff-directory]');
          if (d2) { scrollToEl(ctx, d2, false); focusEl(focusable(qs(d2, 'h2') || d2)); }
        }
        return;
      }
      if (b.hasAttribute('data-abff-change-model')) { e.preventDefault(); openChangePanel(ctx, b); return; }
      if (b.hasAttribute('data-abff-mode-toggle')) { e.preventDefault(); if (ctx.mode === 'multi') exitMulti(ctx, { focus: true }); else enterMulti(ctx); return; }
      if (b.hasAttribute('data-abff-mode-cancel')) { e.preventDefault(); exitMulti(ctx, { focus: true }); ctx.announce(ctx.str('mode_off')); return; }
      if (b.hasAttribute('data-abff-bulk-add')) {
        e.preventDefault();
        if (b.getAttribute('aria-disabled') === 'true' && !ctx.bulkBusy) { ctx.announce(ctx.str('empty')); return; }
        bulkAdd(ctx, b);
        return;
      }
      if (b.hasAttribute('data-abff-retry-load') || (b.hasAttribute('data-abff-retry') && b.closest('[data-abff-panel="error"]'))) { e.preventDefault(); try { window.location.reload(); } catch (err) { /* ignore */ } }
    });
  }

  /* ---------- boot ---------- */
  function boot(scope) {
    qsa(scope || document, '[data-abff]').forEach(function (root) {
      try { initRoot(root); } catch (e) { if (window.console) window.console.error('[abff]', e); }
    });
  }
  disableWebflowAnchorScroll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  window.ABFF = { core: core, init: boot };
  if (window.__abffTest) window.__abffTest = { core: core, init: boot };
})();
